import React, { useState, useEffect } from "react"
import { useAuth } from "../contexts/auth-context"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { Badge } from "../components/ui/badge"
import { Alert, AlertDescription } from "../components/ui/alert"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog"
import { Label } from "../components/ui/label"
import { Input } from "../components/ui/input"
import { Textarea } from "../components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select"
import { SearchableSelect } from "../components/ui/searchable-select"
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from "../components/ui/pagination"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../components/ui/dropdown-menu"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet"
import { User, Mail, Calendar, Shield, CheckCircle, AlertTriangle, Hotel, Bed, Users, DollarSign, Plus, Eye, Clock, MapPin, Phone, CreditCard, FileText, Download, Star, Home, X, AlertCircle, Filter, Search, ChevronLeft, ChevronRight, Tag, Percent, Gift, TrendingUp, BarChart3, LogOut, CheckCircle2, XCircle, Save, Edit, Menu, MessageSquare, Send, Image as ImageIcon, Paperclip, Trash2, Check } from "lucide-react"
import { useNavigate, Link } from "react-router-dom"
import { apiService } from "../lib/api"
import { useToast } from "../hooks/use-toast"
import { toast as sonnerToast } from "sonner"
import { MoonLoader } from "../components/ui/moon-loader"
import { getTenantId, config } from "../lib/config"
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import RoomAvailabilityView from '../components/room-availability-view'

// Brand colors from landing page
const BRAND_COLORS = {
  primary: '#c39d76', // Warm beige/tan
  secondary: '#d4a574', // Lighter tan
  dark: '#2C2416', // Dark brown
  darkGrey: '#3F454A', // Dark grey
  accent: '#dfc9b1', // Light beige
}

// Comprehensive countries list for profile form
const COUNTRIES = [
  // African Countries (prioritized)
  { value: 'RW', label: 'Rwanda' },
  { value: 'KE', label: 'Kenya' },
  { value: 'UG', label: 'Uganda' },
  { value: 'TZ', label: 'Tanzania' },
  { value: 'ET', label: 'Ethiopia' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'NG', label: 'Nigeria' },
  { value: 'GH', label: 'Ghana' },
  { value: 'EG', label: 'Egypt' },
  { value: 'MA', label: 'Morocco' },
  { value: 'DZ', label: 'Algeria' },
  { value: 'TN', label: 'Tunisia' },
  { value: 'SN', label: 'Senegal' },
  { value: 'CI', label: 'Côte d\'Ivoire' },
  { value: 'CM', label: 'Cameroon' },
  { value: 'AO', label: 'Angola' },
  { value: 'SD', label: 'Sudan' },
  { value: 'MZ', label: 'Mozambique' },
  { value: 'MG', label: 'Madagascar' },
  { value: 'ZM', label: 'Zambia' },
  { value: 'ZW', label: 'Zimbabwe' },
  { value: 'ML', label: 'Mali' },
  { value: 'BF', label: 'Burkina Faso' },
  { value: 'NE', label: 'Niger' },
  { value: 'TD', label: 'Chad' },
  { value: 'SO', label: 'Somalia' },
  { value: 'ER', label: 'Eritrea' },
  { value: 'DJ', label: 'Djibouti' },
  { value: 'BI', label: 'Burundi' },
  { value: 'CD', label: 'Democratic Republic of the Congo' },
  { value: 'CG', label: 'Republic of the Congo' },
  { value: 'CF', label: 'Central African Republic' },
  { value: 'SS', label: 'South Sudan' },
  { value: 'LY', label: 'Libya' },
  { value: 'MR', label: 'Mauritania' },
  { value: 'GM', label: 'Gambia' },
  { value: 'GW', label: 'Guinea-Bissau' },
  { value: 'GN', label: 'Guinea' },
  { value: 'SL', label: 'Sierra Leone' },
  { value: 'LR', label: 'Liberia' },
  { value: 'TG', label: 'Togo' },
  { value: 'BJ', label: 'Benin' },
  { value: 'GA', label: 'Gabon' },
  { value: 'GQ', label: 'Equatorial Guinea' },
  { value: 'ST', label: 'São Tomé and Príncipe' },
  { value: 'BW', label: 'Botswana' },
  { value: 'NA', label: 'Namibia' },
  { value: 'LS', label: 'Lesotho' },
  { value: 'SZ', label: 'Eswatini' },
  { value: 'MW', label: 'Malawi' },
  { value: 'MU', label: 'Mauritius' },
  { value: 'SC', label: 'Seychelles' },
  { value: 'KM', label: 'Comoros' },
  { value: 'CV', label: 'Cape Verde' },
  // Major Countries
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'FR', label: 'France' },
  { value: 'DE', label: 'Germany' },
  { value: 'IT', label: 'Italy' },
  { value: 'ES', label: 'Spain' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'BE', label: 'Belgium' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'AT', label: 'Austria' },
  { value: 'SE', label: 'Sweden' },
  { value: 'NO', label: 'Norway' },
  { value: 'DK', label: 'Denmark' },
  { value: 'FI', label: 'Finland' },
  { value: 'PL', label: 'Poland' },
  { value: 'PT', label: 'Portugal' },
  { value: 'GR', label: 'Greece' },
  { value: 'IE', label: 'Ireland' },
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'JP', label: 'Japan' },
  { value: 'CN', label: 'China' },
  { value: 'IN', label: 'India' },
  { value: 'KR', label: 'South Korea' },
  { value: 'SG', label: 'Singapore' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'TH', label: 'Thailand' },
  { value: 'VN', label: 'Vietnam' },
  { value: 'PH', label: 'Philippines' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'BR', label: 'Brazil' },
  { value: 'MX', label: 'Mexico' },
  { value: 'AR', label: 'Argentina' },
  { value: 'CL', label: 'Chile' },
  { value: 'CO', label: 'Colombia' },
  { value: 'PE', label: 'Peru' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'IL', label: 'Israel' },
  { value: 'TR', label: 'Turkey' },
  { value: 'RU', label: 'Russia' },
  { value: 'UA', label: 'Ukraine' },
  { value: 'PK', label: 'Pakistan' },
  { value: 'BD', label: 'Bangladesh' },
  { value: 'LK', label: 'Sri Lanka' },
  { value: 'NP', label: 'Nepal' },
  { value: 'MM', label: 'Myanmar' },
  { value: 'KH', label: 'Cambodia' },
  { value: 'LA', label: 'Laos' },
  { value: 'MN', label: 'Mongolia' },
  { value: 'KZ', label: 'Kazakhstan' },
  { value: 'UZ', label: 'Uzbekistan' },
  { value: 'IQ', label: 'Iraq' },
  { value: 'IR', label: 'Iran' },
  { value: 'AF', label: 'Afghanistan' },
  { value: 'JO', label: 'Jordan' },
  { value: 'LB', label: 'Lebanon' },
  { value: 'SY', label: 'Syria' },
  { value: 'YE', label: 'Yemen' },
  { value: 'OM', label: 'Oman' },
  { value: 'KW', label: 'Kuwait' },
  { value: 'QA', label: 'Qatar' },
  { value: 'BH', label: 'Bahrain' },
  { value: 'CY', label: 'Cyprus' },
  { value: 'MT', label: 'Malta' },
  { value: 'IS', label: 'Iceland' },
  { value: 'LU', label: 'Luxembourg' },
  { value: 'CZ', label: 'Czech Republic' },
  { value: 'HU', label: 'Hungary' },
  { value: 'RO', label: 'Romania' },
  { value: 'BG', label: 'Bulgaria' },
  { value: 'HR', label: 'Croatia' },
  { value: 'SI', label: 'Slovenia' },
  { value: 'SK', label: 'Slovakia' },
  { value: 'EE', label: 'Estonia' },
  { value: 'LV', label: 'Latvia' },
  { value: 'LT', label: 'Lithuania' },
  { value: 'Other', label: 'Other' },
].sort((a, b) => a.label.localeCompare(b.label));

// Helper function to get country code from country name
const getCountryCode = (countryName: string): string => {
  if (!countryName) return '';
  const country = COUNTRIES.find(c => c.label === countryName);
  return country?.value || '';
};

// Currency formatting
const formatCurrency = (amount: number | string, currency: string = 'FRW'): string => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  if (isNaN(numAmount)) return `0 ${currency}`
  
  if (currency === 'FRW') {
    // Format Rwandan Franc without decimals
    return `${Math.round(numAmount).toLocaleString('en-US')} FRW`
  } else {
    // Format other currencies with decimals
    return `${currency} ${numAmount.toFixed(2)}`
  }
}

export default function MyAccount() {
  const { user, logout, updateUser } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Bookings state
  const [bookings, setBookings] = useState<any[]>([])
  const [allBookings, setAllBookings] = useState<any[]>([]) // Store all bookings for filtering
  const [loadingBookings, setLoadingBookings] = useState(false)
  const [showBookingDialog, setShowBookingDialog] = useState(false)
  const [showBookingDetailsDialog, setShowBookingDetailsDialog] = useState(false)
  const [showEditBookingDialog, setShowEditBookingDialog] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [isCancelling, setIsCancelling] = useState(false)
  const [selectedBooking, setSelectedBooking] = useState<any>(null)
  const [isCreatingBooking, setIsCreatingBooking] = useState(false)
  const [isUpdatingBooking, setIsUpdatingBooking] = useState(false)

  // Bookings pagination
  const [bookingsPage, setBookingsPage] = useState(1)
  const [bookingsPageSize] = useState(5)
  const [bookingsTotal, setBookingsTotal] = useState(0)

  // Bookings filters
  const [bookingFilters, setBookingFilters] = useState({
    status: 'all',
    search: '',
    dateFrom: '',
    dateTo: '',
  })

  // Room types and rate plans
  const [roomTypes, setRoomTypes] = useState<any[]>([])
  const [ratePlans, setRatePlans] = useState<any[]>([])
  const [availableRooms, setAvailableRooms] = useState<any[]>([])
  const [allAvailableRooms, setAllAvailableRooms] = useState<any[]>([]) // Store all rooms for filtering
  const [companies, setCompanies] = useState<any[]>([]) // Store companies for filtering
  const [selectedRoom, setSelectedRoom] = useState<any>(null)
  const [showRoomDetailsModal, setShowRoomDetailsModal] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [showImageGallery, setShowImageGallery] = useState(false)
  const [roomsToShow, setRoomsToShow] = useState(8) // Show 8 rooms initially
  const [guestId, setGuestId] = useState<string | null>(null)
  const [isCreatingReservation, setIsCreatingReservation] = useState(false)

  // Promotions
  const [promotions, setPromotions] = useState<any[]>([])
  const [loadingPromotions, setLoadingPromotions] = useState(false)
  const [overviewPromotions, setOverviewPromotions] = useState<any[]>([]) // Separate promotions for overview
  const [loadingOverviewPromotions, setLoadingOverviewPromotions] = useState(false)

  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false)
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    country: '',
    idType: '',
    passportNumber: '',
    idNumber: '',
  })
  const [isSavingProfile, setIsSavingProfile] = useState(false)

  // Messages/Chat state
  const [messages, setMessages] = useState<any[]>([])
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [newMessage, setNewMessage] = useState('')
  const [sendingMessage, setSendingMessage] = useState(false)
  const [currentGuestId, setCurrentGuestId] = useState<string | null>(null)
  const [showMessagesSidebar, setShowMessagesSidebar] = useState(false)
  const [unreadMessageCount, setUnreadMessageCount] = useState(0)
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null)
  const [editingMessageText, setEditingMessageText] = useState('')
  const [editingMessageImageUrl, setEditingMessageImageUrl] = useState<string | null>(null)
  const [editingMessageImagePath, setEditingMessageImagePath] = useState<string | null>(null) // Store original path for reconstruction
  const [editingNewImage, setEditingNewImage] = useState<File | null>(null) // New image selected during edit
  const [editingNewImagePreview, setEditingNewImagePreview] = useState<string | null>(null) // Preview of new image
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null)

  // Helpers to resolve company and guest IDs for messaging
  const getCompanyId = () => (
    allBookings.find((b: any) => b.companyId)?.companyId ||
    localStorage.getItem('company_id') ||
    localStorage.getItem('companyId') ||
    localStorage.getItem('company') ||
    undefined
  )

  const resolveGuestId = async () => {
    let guestId = currentGuestId

    // Try from cached bookings
    if (!guestId && allBookings.length > 0 && user?.email) {
      guestId =
        allBookings.find((b: any) => b.guest?.email === user.email)?.guest?.id ||
        allBookings.find((b: any) => b.guest?.email === user.email)?.guestId ||
        null
    }

    // Try API lookup by email scoped to company
    if (!guestId && user?.email) {
      const companyId = getCompanyId()
      if (companyId) {
        try {
          const guests = await apiService.getHotelGuests({ companyId, search: user.email })
          const guest = Array.isArray(guests) ? guests.find((g: any) => g.email === user.email) : null
          if (guest?.id) {
            guestId = guest.id
          }
        } catch (error) {
          // ignore lookup failures
        }
      }
    }

    if (guestId) {
      setCurrentGuestId(guestId)
    }
    return guestId
  }

  // Rooms pagination
  const [roomsPage, setRoomsPage] = useState(1)
  const [roomsPageSize] = useState(3)
  const [roomsTotal, setRoomsTotal] = useState(0)

  // Rooms filters
  const [roomFilters, setRoomFilters] = useState({
    companyId: 'all',
    roomType: 'all',
    search: '',
    minPrice: '',
    maxPrice: '',
  })

  // Booking form
  const [bookingForm, setBookingForm] = useState({
    checkInDate: '',
    checkOutDate: '',
    numberOfGuests: '1',
    specialRequests: '',
    promotionId: '',
  })
  // Edit booking form (for editing existing reservations)
  const [editBookingForm, setEditBookingForm] = useState({
    checkInDate: '',
    checkOutDate: '',
    numberOfGuests: '1',
    specialRequests: '',
  })
  const [bookingErrors, setBookingErrors] = useState<Record<string, string>>({})
  const [editBookingErrors, setEditBookingErrors] = useState<Record<string, string>>({})
  const [selectedPromotion, setSelectedPromotion] = useState<any>(null)
  const [appliedPromotions, setAppliedPromotions] = useState<any[]>([]) // Store all applied promotions

  // Reviews state
  const [reviews, setReviews] = useState<any[]>([])
  const [loadingReviews, setLoadingReviews] = useState(false)
  const [showReviewDialog, setShowReviewDialog] = useState(false)
  const [isCreatingReview, setIsCreatingReview] = useState(false)
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    comment: '',
  })
  const [reviewErrors, setReviewErrors] = useState<Record<string, string>>({})

  // Redirect if not authenticated
  useEffect(() => {
    if (!user) {
      navigate("/")
      return
    }

    // Load initial data
    loadBookings()
    loadRoomTypes()
    loadRatePlans()
    loadAvailableRooms() // Load available rooms immediately
    loadOverviewPromotions() // Load promotions for overview
    // Don't load booking promotions here - will load when booking dialog opens with companyId
  }, [user, navigate])

  // Reload available rooms when switching to new-booking tab
  // DON'T load messages on mount - only when sidebar opens
  // This prevents marking messages as read before user sees them
  useEffect(() => {
    if (user) {
      // Don't call loadMessages() here - it will mark messages as read!
      // Instead, only use loadUnreadCount() which doesn't mark as read
    }
  }, [user, allBookings])

  // Helper: load unread count and capture guestId if found
  const loadUnreadCount = async () => {
    try {
      let guestId = currentGuestId || (await resolveGuestId())

      // If we have a guestId, check messages for that guest
      if (guestId) {
        const messagesData = await apiService.getGuestMessages({ guestId })
        const unreadCount = messagesData ? messagesData.filter((m: any) => !m.isFromGuest && !m.isRead).length : 0
        setUnreadMessageCount(unreadCount)
        return
      }

      // If no guestId yet, try to load messages to get it and check unread count
      const messagesData = await apiService.getGuestMessages(undefined)
      const unreadCount = messagesData ? messagesData.filter((m: any) => !m.isFromGuest && !m.isRead).length : 0
      setUnreadMessageCount(unreadCount)

      // Capture guestId from first message if present
      if (!guestId && messagesData && messagesData.length > 0) {
        const firstMessage = messagesData[0]
        if (firstMessage.guest?.id) {
          guestId = firstMessage.guest.id
          setCurrentGuestId(guestId)
        }
      }
    } catch (error) {
      // Silently fail for periodic updates
    }
  }

  // Load unread count periodically (every 15 seconds)
  useEffect(() => {
    if (!user) return

    // Load immediately
    loadUnreadCount()

    const interval = setInterval(loadUnreadCount, 15000) // Check every 15 seconds
    return () => clearInterval(interval)
  }, [user, currentGuestId]) // Add currentGuestId as dependency so it re-runs when guestId is found

  // Load messages when sidebar opens
  useEffect(() => {
    if (user && showMessagesSidebar) {
      loadMessages()
      loadUnreadCount()
    }
  }, [user, showMessagesSidebar])

  useEffect(() => {
    if (activeTab === 'new-booking') {
      loadAvailableRooms()
    }
  }, [activeTab])

  // Reload overview promotions when switching to overview tab
  useEffect(() => {
    if (activeTab === 'overview') {
      loadOverviewPromotions()
    }
  }, [activeTab])

  // Load reviews when switching to reviews tab
  useEffect(() => {
    if (activeTab === 'reviews') {
      loadReviews()
    }
  }, [activeTab])

  // Initialize profile form when user data is available
  useEffect(() => {
    if (user) {
      // Determine ID type based on which field has a value
      let idType = '';
      if ((user as any).passportNumber) {
        idType = 'passport';
      } else if ((user as any).idNumber) {
        idType = 'national_id';
      }

      setProfileForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        country: getCountryCode((user as any).country || ''),
        idType: idType,
        passportNumber: (user as any).passportNumber || '',
        idNumber: (user as any).idNumber || '',
      })
    }
  }, [user])

  // Handle profile edit
  const handleEditProfile = () => {
    setIsEditingProfile(true)
  }

  // Handle profile save
  const handleSaveProfile = async () => {
    if (!user) return

    // Validate required fields
    if (!profileForm.firstName.trim()) {
      toast({
        title: 'Validation Error',
        description: 'First name is required',
        variant: 'destructive',
      })
      return
    }

    setIsSavingProfile(true)
    try {
      // Convert country code to country name for storage
      const countryName = profileForm.country
        ? COUNTRIES.find(c => c.value === profileForm.country)?.label || profileForm.country
        : '';

      // Prepare update data
      const updateData: any = {
        firstName: profileForm.firstName.trim(),
        lastName: profileForm.lastName.trim(),
        phone: profileForm.phone.trim() || undefined,
      };

      // Add country if provided
      if (countryName) {
        updateData.country = countryName;
      }

      // Add ID fields based on selected type
      if (profileForm.idType === 'passport' && profileForm.passportNumber) {
        updateData.passportNumber = profileForm.passportNumber.trim();
        updateData.idNumber = null; // Clear idNumber if passport is selected
      } else if (profileForm.idType === 'national_id' && profileForm.idNumber) {
        updateData.idNumber = profileForm.idNumber.trim();
        updateData.passportNumber = null; // Clear passportNumber if national ID is selected
      }

      const response = await apiService.updateCurrentUserProfile(updateData)

      // Update user context with new values from API response
      if (updateUser) {
        await updateUser({
          firstName: response?.firstName || profileForm.firstName.trim(),
          lastName: response?.lastName || profileForm.lastName.trim(),
          phone: response?.phone || profileForm.phone.trim() || undefined,
          country: countryName || (response as any)?.country,
          passportNumber: (response as any)?.passportNumber,
          idNumber: (response as any)?.idNumber,
        } as any)
      }

      setIsEditingProfile(false)
      toast({
        title: 'Success',
        description: 'Profile updated successfully',
      })
    } catch (error: any) {
      console.error('Failed to update profile:', error)
      toast({
        title: 'Error',
        description: error.message || 'Failed to update profile',
        variant: 'destructive',
      })
    } finally {
      setIsSavingProfile(false)
    }
  }

  // Handle profile cancel
  const handleCancelEdit = () => {
    if (user) {
      // Determine ID type based on which field has a value
      let idType = '';
      if ((user as any).passportNumber) {
        idType = 'passport';
      } else if ((user as any).idNumber) {
        idType = 'national_id';
      }

      setProfileForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        country: getCountryCode((user as any).country || ''),
        idType: idType,
        passportNumber: (user as any).passportNumber || '',
        idNumber: (user as any).idNumber || '',
      })
    }
    setIsEditingProfile(false)
  }

  // Load messages for guest
  const loadMessages = async () => {
    if (!user?.email) return

    setLoadingMessages(true)
    try {
      // Try to find guest ID first (for efficiency)
      let guestId = currentGuestId ||
        allBookings.find((b: any) => b.guest?.email === user.email)?.guest?.id ||
        allBookings.find((b: any) => b.guest?.email === user.email)?.guestId

      // If no guestId found, try to find by searching guests
      if (!guestId) {
        try {
          const companyId = localStorage.getItem('company_id') ||
            localStorage.getItem('companyId') ||
            localStorage.getItem('company') ||
            allBookings.find((b: any) => b.companyId)?.companyId

          if (companyId) {
            const guests = await apiService.getHotelGuests({ companyId, search: user.email })
            const guest = Array.isArray(guests)
              ? guests.find((g: any) => g.email === user.email)
              : null
            if (guest?.id) {
              guestId = guest.id
              setCurrentGuestId(guest.id)
            }
          }
        } catch (error) {
          console.error('Failed to find guest:', error)
        }
      } else {
        setCurrentGuestId(guestId)
      }

      // Load messages - backend will find guest by email if guestId is not provided
      const messagesData = await apiService.getGuestMessages(guestId ? { guestId } : undefined)
      setMessages(messagesData || [])

      // Extract guestId from messages if we don't have it yet
      if (!guestId && messagesData && messagesData.length > 0) {
        const firstMessage = messagesData[0]
        if (firstMessage.guest?.id) {
          guestId = firstMessage.guest.id
          setCurrentGuestId(guestId)
        }
      }

      // Count unread messages (messages from admin/receptionist that are not read)
      const unreadCount = messagesData ? messagesData.filter((m: any) => !m.isFromGuest && !m.isRead).length : 0

      // Always set unread count first (so badge shows even when sidebar is closed)
      setUnreadMessageCount(unreadCount)

      // Only mark messages as read when sidebar is open (user is actively viewing messages)
      // This ensures the unread count badge shows correctly when sidebar is closed
      if (guestId && showMessagesSidebar && unreadCount > 0) {
        try {
          await apiService.markAllMessagesAsRead(guestId)
          // Reload messages to get updated isRead status and update unread count
          const updatedMessages = await apiService.getGuestMessages({ guestId })
          const updatedUnreadCount = updatedMessages ? updatedMessages.filter((m: any) => !m.isFromGuest && !m.isRead).length : 0
          setUnreadMessageCount(updatedUnreadCount)
          setMessages(updatedMessages || [])
        } catch (error) {
          // Silently fail - marking as read is not critical
          console.error('Failed to mark messages as read:', error)
        }
      }
    } catch (error: any) {
      console.error('Failed to load messages:', error)
      toast({
        title: 'Error',
        description: error.message || 'Failed to load messages',
        variant: 'destructive',
      })
    } finally {
      setLoadingMessages(false)
    }
  }

  // Handle image selection
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          title: 'Error',
          description: 'Image size must be less than 5MB',
          variant: 'destructive',
        })
        return
      }
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Error',
          description: 'Please select an image file',
          variant: 'destructive',
        })
        return
      }
      setSelectedImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  // Remove selected image
  const handleRemoveImage = () => {
    setSelectedImage(null)
    setImagePreview(null)
  }

  // Convert image to base64
  const imageToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = error => reject(error)
    })
  }

  // Send a message
  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !selectedImage) || sendingMessage) return

    setSendingMessage(true)
    try {
      let messageText = newMessage.trim()

      // If image is selected, convert to base64 and append to message
      if (selectedImage) {
        try {
          const base64Image = await imageToBase64(selectedImage)
          if (messageText) {
            messageText = `${messageText}\n[IMAGE:${base64Image}]`
          } else {
            messageText = `[IMAGE:${base64Image}]`
          }
        } catch (error) {
          console.error('Failed to convert image:', error)
          toast({
            title: 'Error',
            description: 'Failed to process image',
            variant: 'destructive',
          })
          setSendingMessage(false)
          return
        }
      }

      // Get companyId from bookings or localStorage (optional - backend will handle if not provided)
      const companyId = allBookings.find((b: any) => b.companyId)?.companyId ||
        localStorage.getItem('company_id') ||
        localStorage.getItem('companyId') ||
        localStorage.getItem('company') ||
        undefined

      const response = await apiService.createGuestMessage({
        guestId: currentGuestId || undefined,
        messageText: messageText,
        isFromGuest: true,
        companyId: companyId,
      })

      // Update guestId from response if we don't have it yet
      if (!currentGuestId && response?.guest?.id) {
        setCurrentGuestId(response.guest.id)
      }

      setNewMessage('')
      setSelectedImage(null)
      setImagePreview(null)
      await loadMessages() // Reload messages
    } catch (error: any) {
      console.error('Failed to send message:', error)
      toast({
        title: 'Error',
        description: error.message || 'Failed to send message',
        variant: 'destructive',
      })
    } finally {
      setSendingMessage(false)
    }
  }

  // Handle edit message
  const handleEditMessage = (message: any) => {
    // Extract image URL from message text
    let imageUrl: string | null = null
    let textWithoutImage = message.messageText || ''
    let originalImagePath: string | null = null // Store original path for reconstruction

    if (message.messageText) {
      // First, check for [IMAGE:...] pattern (legacy base64) - check this first as it's more specific
      const imageMatch = message.messageText.match(/\[IMAGE:([\s\S]*?)\]/)
      if (imageMatch && imageMatch[1]) {
        const extracted = imageMatch[1].trim()
        if (extracted.startsWith('data:image/')) {
          imageUrl = extracted
        } else if (extracted.startsWith('data:')) {
          imageUrl = extracted
        } else {
          let mimeType = 'image/jpeg'
          if (extracted.startsWith('/9j/') || extracted.startsWith('iVBORw0KGgo')) {
            mimeType = extracted.startsWith('/9j/') ? 'image/jpeg' : 'image/png'
          }
          imageUrl = `data:${mimeType};base64,${extracted}`
        }
        textWithoutImage = message.messageText.replace(/\[IMAGE:[\s\S]*?\]/, '').trim()
      } else {
        // Check for MongoDB GridFS image URL pattern: /api/images/{fileId}
        const patterns = [
          /\/api\/images\/([a-fA-F0-9]{24})/,
          /\/api\/images\/([a-fA-F0-9]+)/,
          /api\/images\/([a-fA-F0-9]{24})/,
          /api\/images\/([a-fA-F0-9]+)/,
        ]

        let gridFsMatch = null
        for (const pattern of patterns) {
          gridFsMatch = message.messageText.match(pattern)
          if (gridFsMatch) break
        }

        if (gridFsMatch) {
          // Store original path for reconstruction
          originalImagePath = gridFsMatch[0]

          // Construct full URL for display
          let imagePath = gridFsMatch[0]
          if (!imagePath.startsWith('/')) {
            imagePath = '/' + imagePath
          }

          const baseUrl = (apiService as any)['baseUrl'] || config.api.baseUrl || (typeof window !== 'undefined' ? window.location.origin : '')
          imageUrl = imagePath.startsWith('http')
            ? imagePath
            : imagePath.startsWith('/')
              ? `${baseUrl}${imagePath}`
              : `${baseUrl}/${imagePath}`

          // Remove the image URL from text - be more aggressive
          const escapedMatch = gridFsMatch[0].replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
          textWithoutImage = message.messageText
            .replace(new RegExp(escapedMatch, 'g'), '')
            .replace(/\n\n+/g, '\n')
            .replace(/^\n+|\n+$/g, '')
            .trim()
        }
      }
    }

    setEditingMessageId(message.id)
    setEditingMessageText(textWithoutImage)
    setEditingMessageImageUrl(imageUrl)
    setEditingMessageImagePath(originalImagePath)
  }

  // Handle save edited message
  const handleSaveEdit = async (messageId: string) => {
    // If message has no text and no image (old or new), show error
    if (!editingMessageText.trim() && !editingMessageImageUrl && !editingNewImage) {
      toast({
        title: 'Error',
        description: 'Message cannot be empty',
        variant: 'destructive',
      })
      return
    }

    try {
      let finalMessageText = editingMessageText.trim()

      // If a new image is selected, upload it and use it (replaces old image)
      if (editingNewImage) {
        try {
          const base64Image = await imageToBase64(editingNewImage)
          if (finalMessageText) {
            finalMessageText = `${finalMessageText}\n[IMAGE:${base64Image}]`
          } else {
            finalMessageText = `[IMAGE:${base64Image}]`
          }
        } catch (error) {
          console.error('Failed to convert image:', error)
          toast({
            title: 'Error',
            description: 'Failed to process image',
            variant: 'destructive',
          })
          return
        }
      } else if (editingMessageImageUrl) {
        // If no new image, preserve the old image
        // If it's a data URL, use [IMAGE:...] format
        if (editingMessageImageUrl.startsWith('data:')) {
          finalMessageText = finalMessageText
            ? `${finalMessageText}\n[IMAGE:${editingMessageImageUrl}]`
            : `[IMAGE:${editingMessageImageUrl}]`
        } else if (editingMessageImagePath) {
          // Use the original path we stored
          finalMessageText = finalMessageText
            ? `${finalMessageText}\n${editingMessageImagePath}`
            : editingMessageImagePath
        } else {
          // Extract /api/images/... path from full URL as fallback
          const imagePathMatch = editingMessageImageUrl.match(/(\/api\/images\/[a-fA-F0-9]+|api\/images\/[a-fA-F0-9]+)/)
          if (imagePathMatch) {
            const imagePath = imagePathMatch[0]
            finalMessageText = finalMessageText
              ? `${finalMessageText}\n${imagePath}`
              : imagePath
          } else {
            // Fallback: use full URL
            finalMessageText = finalMessageText
              ? `${finalMessageText}\n${editingMessageImageUrl}`
              : editingMessageImageUrl
          }
        }
      }

      await apiService.updateGuestMessage(messageId, finalMessageText)
      toast({
        title: 'Success',
        description: 'Message updated successfully',
      })

      // Reload messages
      await loadMessages()

      setEditingMessageId(null)
      setEditingMessageText('')
      setEditingMessageImageUrl(null)
      setEditingMessageImagePath(null)
      setEditingNewImage(null)
      setEditingNewImagePreview(null)
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update message',
        variant: 'destructive',
      })
    }
  }

  // Handle cancel edit message
  const handleCancelEditMessage = () => {
    setEditingMessageId(null)
    setEditingMessageText('')
    setEditingMessageImageUrl(null)
    setEditingMessageImagePath(null)
    setEditingNewImage(null)
    setEditingNewImagePreview(null)
  }

  // Handle image selection for editing
  const handleEditImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && file.type.startsWith('image/')) {
      setEditingNewImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setEditingNewImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  // Remove new image from edit
  const handleRemoveEditImage = () => {
    setEditingNewImage(null)
    setEditingNewImagePreview(null)
  }

  // Handle delete message
  const handleDeleteMessage = async (messageId: string) => {
    if (!confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
      return
    }

    setDeletingMessageId(messageId)
    try {
      await apiService.deleteGuestMessage(messageId)
      toast({
        title: 'Success',
        description: 'Message deleted successfully',
      })

      // Reload messages
      await loadMessages()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete message',
        variant: 'destructive',
      })
    } finally {
      setDeletingMessageId(null)
    }
  }

  // Check if user can edit/delete a message (only their own messages - isFromGuest must be true)
  const canEditDeleteMessage = (message: any) => {
    // Guests can only edit/delete their own messages (isFromGuest must be true)
    return message.isFromGuest === true
  }

  // Load user's reviews
  const loadReviews = async () => {
    if (!user?.email) return

    setLoadingReviews(true)
    try {
      // Try multiple ways to get guest ID:
      // 1. From allBookings if available
      let guestId = allBookings.find((b: any) => b.guest?.email === user.email)?.guest?.id ||
        allBookings.find((b: any) => b.guest?.email === user.email)?.guestId

      // 2. If not found, try to find guest by email from API
      if (!guestId) {
        try {
          // Get company ID from localStorage or first booking
          const companyId = localStorage.getItem('company_id') ||
            localStorage.getItem('companyId') ||
            localStorage.getItem('company') ||
            allBookings.find((b: any) => b.companyId)?.companyId

          if (companyId) {
            const guests = await apiService.getHotelGuests({ companyId, search: user.email })
            const guest = Array.isArray(guests)
              ? guests.find((g: any) => g.email === user.email)
              : null
            if (guest?.id) {
              guestId = guest.id
            }
          }
        } catch (guestError) {
          console.error('Failed to find guest:', guestError)
        }
      }

      // 3. If still not found, try searching across all companies
      if (!guestId) {
        try {
          // Try to get reviews by searching with email - backend should handle this
          // For now, just try to get all reviews and filter client-side
          const allReviews = await apiService.getHotelReviews({})
          const userReviews = allReviews.filter((r: any) =>
            r.guest?.email === user.email
          )
          setReviews(userReviews)
          return
        } catch (searchError) {
          console.error('Failed to search reviews:', searchError)
        }
      }

      // If we have guestId, fetch reviews
      if (guestId) {
        const reviewsData = await apiService.getHotelReviews({ guestId })
        setReviews(reviewsData)
      } else {
        setReviews([])
      }
    } catch (error) {
      console.error('Failed to load reviews:', error)
      toast({
        title: "Error",
        description: "Failed to load your reviews",
        variant: "destructive",
      })
    } finally {
      setLoadingReviews(false)
    }
  }

  // Open review dialog for a booking
  const handleOpenReviewDialog = (booking?: any) => {
    if (booking) {
      setReviewForm({
        rating: 5,
        comment: '',
      })
      // Store booking info for review submission
      setSelectedBooking(booking)
    } else {
      setReviewForm({
        rating: 5,
        comment: '',
      })
      setSelectedBooking(null)
    }
    setReviewErrors({})
    setShowReviewDialog(true)
  }

  // Validate review
  const validateReview = (): boolean => {
    const errors: Record<string, string> = {}

    if (!reviewForm.rating || reviewForm.rating < 1 || reviewForm.rating > 5) {
      errors.rating = 'Please provide a rating between 1 and 5'
    }

    if (!selectedBooking) {
      errors.booking = 'Please select a booking to review'
    }

    setReviewErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Submit review
  const handleCreateReview = async () => {
    if (!validateReview() || !selectedBooking) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly',
        variant: 'destructive',
      })
      return
    }

    // Get guestId from reservation - prefer direct guestId field, then guest.id, then search in allBookings
    const guestId = selectedBooking.guestId ||
      selectedBooking.guest?.id ||
      allBookings.find((b: any) => b.id === selectedBooking.id)?.guestId ||
      allBookings.find((b: any) => b.id === selectedBooking.id)?.guest?.id

    if (!guestId || !selectedBooking.roomId) {
      toast({
        title: 'Error',
        description: 'Missing guest or room information. Please try refreshing the page.',
        variant: 'destructive',
      })
      return
    }

    setIsCreatingReview(true)
    setReviewErrors({})

    try {
      await apiService.createHotelReview({
        guestId: guestId,
        roomId: selectedBooking.roomId,
        reservationId: selectedBooking.id,
        rating: reviewForm.rating,
        comment: reviewForm.comment || undefined,
      })

      toast({
        title: 'Success',
        description: 'Review submitted successfully',
      })

      setShowReviewDialog(false)
      setReviewForm({
        rating: 5,
        comment: '',
      })
      setSelectedBooking(null)
      loadReviews()
      loadBookings() // Reload bookings to refresh review status
    } catch (error: any) {
      console.error('Failed to create review:', error)

      // Extract error message from various possible locations
      let errorMessage = 'Failed to submit review. Please try again.'
      if (error.message) {
        errorMessage = error.message
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message
      } else if (error.data?.message) {
        errorMessage = error.data.message
      } else if (error.data?.error?.message) {
        errorMessage = error.data.error.message
      }

      // Use sonner toast for better visibility
      sonnerToast.error(errorMessage, {
        duration: 5000,
        style: {
          zIndex: 999999,
        },
      })

      // Also show the regular toast as fallback
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
        duration: 5000,
      })
    } finally {
      setIsCreatingReview(false)
    }
  }

  // Check if booking has review
  const hasReview = (bookingId: string): boolean => {
    return reviews.some((r: any) => r.reservationId === bookingId)
  }

  // Load user's bookings
  const loadBookings = async () => {
    if (!user?.email) return

    setLoadingBookings(true)
    try {
      const allReservations = await apiService.getHotelReservations({})
      // Filter bookings for current user by email
      const userBookings = allReservations.filter((r: any) =>
        r.guest?.email === user.email || r.guestEmail === user.email
      )
      setAllBookings(userBookings)
      applyBookingsFilters(userBookings)
    } catch (error) {
      console.error('Failed to load bookings:', error)
      toast({
        title: "Error",
        description: "Failed to load your bookings",
        variant: "destructive",
      })
    } finally {
      setLoadingBookings(false)
    }
  }

  // Apply filters and pagination to bookings
  const applyBookingsFilters = (allBookingsData: any[]) => {
    let filtered = [...allBookingsData]

    // Filter by status
    if (bookingFilters.status !== 'all') {
      filtered = filtered.filter((b: any) => b.status === bookingFilters.status)
    }

    // Filter by search (room number, room type, reservation number)
    if (bookingFilters.search) {
      const searchLower = bookingFilters.search.toLowerCase()
      filtered = filtered.filter((b: any) =>
        b.room?.roomNumber?.toLowerCase().includes(searchLower) ||
        b.room?.roomType?.name?.toLowerCase().includes(searchLower) ||
        b.reservationNumber?.toLowerCase().includes(searchLower) ||
        b.room?.title?.toLowerCase().includes(searchLower)
      )
    }

    // Filter by date range
    if (bookingFilters.dateFrom) {
      const fromDate = new Date(bookingFilters.dateFrom)
      filtered = filtered.filter((b: any) =>
        new Date(b.checkInDate) >= fromDate || new Date(b.checkOutDate) >= fromDate
      )
    }
    if (bookingFilters.dateTo) {
      const toDate = new Date(bookingFilters.dateTo)
      filtered = filtered.filter((b: any) =>
        new Date(b.checkInDate) <= toDate || new Date(b.checkOutDate) <= toDate
      )
    }

    // Sort by check-in date (newest first)
    filtered.sort((a: any, b: any) =>
      new Date(b.checkInDate).getTime() - new Date(a.checkInDate).getTime()
    )

    setBookingsTotal(filtered.length)

    // Apply pagination
    const startIndex = (bookingsPage - 1) * bookingsPageSize
    const endIndex = startIndex + bookingsPageSize
    setBookings(filtered.slice(startIndex, endIndex))
  }

  // Update bookings when filters or page change
  useEffect(() => {
    if (allBookings.length > 0) {
      applyBookingsFilters(allBookings)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingFilters.status, bookingFilters.search, bookingFilters.dateFrom, bookingFilters.dateTo, bookingsPage, allBookings.length])

  // Reset to page 1 when filters change
  useEffect(() => {
    setBookingsPage(1)
  }, [bookingFilters.status, bookingFilters.search, bookingFilters.dateFrom, bookingFilters.dateTo])

  // Load room types
  const loadRoomTypes = async () => {
    try {
      const types = await apiService.getHotelRoomTypes()
      setRoomTypes(types)
    } catch (error) {
      console.error('Failed to load room types:', error)
    }
  }

  // Load rate plans
  const loadRatePlans = async () => {
    try {
      const plans = await apiService.getHotelRatePlans()
      setRatePlans(plans)
    } catch (error) {
      console.error('Failed to load rate plans:', error)
    }
  }

  // Load active promotions for a specific company
  const loadPromotions = async (companyId?: string) => {
    if (!companyId) {
      setPromotions([])
      return
    }

    setLoadingPromotions(true)
    try {
      // Load promotions for the specific company only
      const allPromotions = await apiService.getPromotions({
        isActive: true,
        companyId
      })

      // Filter promotions that are currently valid (between validFrom and validTo)
      const now = new Date()

      const activePromotions = allPromotions.filter((promo: any) => {
        const validFrom = new Date(promo.validFrom)
        const validTo = new Date(promo.validTo)
        const isValid = now >= validFrom && now <= validTo
        return isValid
      })

      setPromotions(activePromotions)

      // Auto-apply promotion if booking form has dates
      if (selectedRoom && bookingForm.checkInDate && bookingForm.checkOutDate) {
        setTimeout(() => {
          autoApplyPromotion(bookingForm.checkInDate, bookingForm.checkOutDate, activePromotions)
        }, 100)
      }
    } catch (error) {
      console.error('Failed to load promotions:', error)
      setPromotions([])
    } finally {
      setLoadingPromotions(false)
    }
  }

  // Prepare chart data
  const getMonthlyBookingsData = () => {
    const last6Months = []
    const now = new Date()

    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const monthName = date.toLocaleDateString('en-US', { month: 'short' })
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1)
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0)

      const monthBookings = allBookings.filter((booking: any) => {
        const bookingDate = new Date(booking.createdAt || booking.checkInDate)
        return bookingDate >= monthStart && bookingDate <= monthEnd
      })

      last6Months.push({
        month: monthName,
        bookings: monthBookings.length,
        confirmed: monthBookings.filter((b: any) => b.status === 'CONFIRMED').length,
        completed: monthBookings.filter((b: any) => b.status === 'CHECKED_OUT').length,
      })
    }

    return last6Months
  }

  const getStatusDistributionData = () => {
    const statusCounts: Record<string, number> = {}

    allBookings.forEach((booking: any) => {
      const status = booking.status || 'UNKNOWN'
      statusCounts[status] = (statusCounts[status] || 0) + 1
    })

    const statusColors: Record<string, string> = {
      'PENDING': '#f59e0b',
      'CONFIRMED': '#10b981',
      'CHECKED_IN': '#3b82f6',
      'CHECKED_OUT': '#8b5cf6',
      'CANCELLED': '#ef4444',
      'NO_SHOW': '#6b7280',
    }

    return Object.entries(statusCounts).map(([status, count]) => ({
      name: status.replace('_', ' '),
      value: count,
      color: statusColors[status] || BRAND_COLORS.primary,
    }))
  }

  // Get room image helper (similar to accommodation.tsx)
  const getRoomImage = (room: any): string => {
    if (room.images && room.images.length > 0) {
      const primaryImage = room.images.find((img: any) => img.isPrimary)
      const imageUrl = primaryImage?.imageUrl || room.images[0]?.imageUrl

      if (imageUrl) {
        if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
          return imageUrl
        }
        const baseUrl = apiService['baseUrl'] || ''
        return imageUrl.startsWith("/") ? `${baseUrl}${imageUrl}` : `${baseUrl}/${imageUrl}`
      }
    }
    return "/images/webflow/IWACU-002.jpg"
  }

  // Get room amenities helper
  const getRoomAmenities = (room: any): string => {
    if (!room.amenities || room.amenities.length === 0) return ''
    return room.amenities.map((a: any) => a.amenity?.name || '').filter(Boolean).join(', ')
  }

  // Check if a room is reserved
  const isRoomReserved = (room: any): boolean => {
    if (!room.reservations || room.reservations.length === 0) return false
    return room.reservations.some((r: any) =>
      r.status !== 'CANCELLED' && r.status !== 'CHECKED_OUT'
    )
  }

  // Get booked dates for a room (formatted string)
  const getRoomBookedDates = (room: any): string => {
    if (!room.reservations || room.reservations.length === 0) return ''

    const activeReservations = room.reservations
      .filter((r: any) => r.status !== 'CANCELLED' && r.status !== 'CHECKED_OUT')
      .sort((a: any, b: any) =>
        new Date(a.checkInDate).getTime() - new Date(b.checkInDate).getTime()
      )

    if (activeReservations.length === 0) return ''

    // Format dates: "Jan 15 - Jan 20, Jan 25 - Jan 30"
    const dateRanges = activeReservations.map((res: any) => {
      const checkIn = new Date(res.checkInDate)
      const checkOut = new Date(res.checkOutDate)
      const formatDate = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      return `${formatDate(checkIn)} - ${formatDate(checkOut)}`
    })

    return dateRanges.join(', ')
  }

  // Image navigation functions for room details modal
  const getImageUrl = (image: any) => {
    if (!image || !image.imageUrl) return '/images/webflow/IWACU-002.jpg';
    if (image.imageUrl.startsWith('http')) {
      return image.imageUrl;
    }
    return image.imageUrl.startsWith('/')
      ? `${apiService['baseUrl']}${image.imageUrl}`
      : `${apiService['baseUrl']}/${image.imageUrl}`;
  };

  const nextImage = () => {
    if (selectedRoom && selectedRoom.images && selectedRoom.images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % selectedRoom.images.length);
    }
  };

  const prevImage = () => {
    if (selectedRoom && selectedRoom.images && selectedRoom.images.length > 0) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? selectedRoom.images.length - 1 : prev - 1
      );
    }
  };

  // Load all rooms (including reserved ones) - same as admin hotel reservations form
  const loadAvailableRooms = async () => {
    try {
      // Get tenantId dynamically - uses user's tenantId if logged in, falls back to tenant_demo
      const tenantId = getTenantId()

      // Use public endpoint that doesn't require authentication
      // Match accommodation.tsx exactly - no headers, simple fetch
      const url = `${apiService['baseUrl']}/api/public/accommodations?tenantId=${tenantId}`

      const response = await fetch(url)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ [MY-ACCOUNT] Failed to fetch accommodations:', response.status, response.statusText, errorText)
        throw new Error(`Failed to fetch accommodations: ${response.statusText}`)
      }

      const data = await response.json()

      if (data.success && data.data) {
        const allRooms = data.data.rooms || []

        // Extract unique companies from rooms
        const uniqueCompanies = new Map()
        allRooms.forEach((room: any) => {
          if (room.company && room.company.id && room.company.name) {
            uniqueCompanies.set(room.company.id, room.company)
          }
        })
        setCompanies(Array.from(uniqueCompanies.values()))

        setAllAvailableRooms(allRooms)
        applyRoomsFilters(allRooms)
      } else {
        setAllAvailableRooms([])
        setAvailableRooms([])
      }
    } catch (error) {
      console.error('Failed to load available rooms:', error)
      setAllAvailableRooms([])
      setAvailableRooms([])
      toast({
        title: "Error",
        description: "Failed to load available rooms",
        variant: "destructive",
      })
    }
  }

  // Apply filters and pagination to rooms
  const applyRoomsFilters = (allRoomsData: any[]) => {
    let filtered = [...allRoomsData]

    // Filter by active status first (same as accommodations page)
    filtered = filtered.filter((r: any) => r.isActive)

    // Filter by company
    if (roomFilters.companyId !== 'all') {
      filtered = filtered.filter((r: any) =>
        r.companyId === roomFilters.companyId || r.company?.id === roomFilters.companyId
      )
    }

    // Filter by room type
    if (roomFilters.roomType !== 'all') {
      filtered = filtered.filter((r: any) =>
        r.roomTypeId === roomFilters.roomType || r.roomTypeRef?.id === roomFilters.roomType
      )
    }

    // Filter by search (room number, title, description)
    if (roomFilters.search) {
      const searchLower = roomFilters.search.toLowerCase()
      filtered = filtered.filter((r: any) =>
        r.roomNumber?.toLowerCase().includes(searchLower) ||
        r.title?.toLowerCase().includes(searchLower) ||
        r.description?.toLowerCase().includes(searchLower) ||
        r.roomTypeRef?.name?.toLowerCase().includes(searchLower)
      )
    }

    // Filter by price range
    if (roomFilters.minPrice) {
      const minPrice = parseFloat(roomFilters.minPrice)
      if (!isNaN(minPrice)) {
        filtered = filtered.filter((r: any) =>
          parseFloat(r.basePricePerNight?.toString() || '0') >= minPrice
        )
      }
    }
    if (roomFilters.maxPrice) {
      const maxPrice = parseFloat(roomFilters.maxPrice)
      if (!isNaN(maxPrice)) {
        filtered = filtered.filter((r: any) =>
          parseFloat(r.basePricePerNight?.toString() || '0') <= maxPrice
        )
      }
    }

    setRoomsTotal(filtered.length)

    // Show all rooms without pagination
    setAvailableRooms(filtered)
  }

  // Update rooms when filters or page change
  useEffect(() => {
    if (allAvailableRooms.length > 0) {
      applyRoomsFilters(allAvailableRooms)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomFilters.companyId, roomFilters.roomType, roomFilters.search, roomFilters.minPrice, roomFilters.maxPrice, roomsPage, allAvailableRooms.length])

  // Reset to page 1 when filters change
  useEffect(() => {
    setRoomsPage(1)
  }, [roomFilters.companyId, roomFilters.roomType, roomFilters.search, roomFilters.minPrice, roomFilters.maxPrice])

  // Reset roomsToShow when filters change or rooms are loaded
  useEffect(() => {
    setRoomsToShow(8)
  }, [roomFilters.companyId, roomFilters.roomType, roomFilters.search, roomFilters.minPrice, roomFilters.maxPrice, availableRooms.length])

  // Handle booking form changes
  const handleBookingFormChange = (field: string, value: any) => {
    setBookingForm(prev => ({ ...prev, [field]: value }))
    setBookingErrors(prev => ({ ...prev, [field]: '' }))
  }

  // Automatically find and apply the best valid promotion
  const autoApplyPromotion = (checkInDate: string, checkOutDate: string, availablePromotions?: any[]) => {
    const promosToCheck = availablePromotions || promotions
    if (!checkInDate || !checkOutDate || !selectedRoom || promosToCheck.length === 0) {
      setSelectedPromotion(null)
      setBookingForm(prev => ({ ...prev, promotionId: '' }))
      return
    }

    const now = new Date()
    const checkIn = new Date(checkInDate)
    const nights = Math.ceil(
      (new Date(checkOutDate).getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24)
    )

    // Filter valid promotions
    const validPromotions = promosToCheck.filter((promo: any) => {
      const validFrom = new Date(promo.validFrom)
      const validTo = new Date(promo.validTo)

      // Check if promotion is active and valid
      if (!promo.isActive) return false
      if (validFrom > now || validTo < now) return false
      if (checkIn < validFrom || checkIn > validTo) return false

      // Check min/max stay
      if (promo.minStay && nights < promo.minStay) return false
      if (promo.maxStay && nights > promo.maxStay) return false

      // Check max uses
      if (promo.maxUses && promo.currentUses >= promo.maxUses) return false

      // Check applicable room types
      if (promo.applicableRoomTypes && Array.isArray(promo.applicableRoomTypes) && promo.applicableRoomTypes.length > 0) {
        if (!promo.applicableRoomTypes.includes(selectedRoom.roomTypeId)) return false
      }

      return true
    })

    if (validPromotions.length === 0) {
      setSelectedPromotion(null)
      setAppliedPromotions([])
      setBookingForm(prev => ({ ...prev, promotionId: '' }))
      return
    }

    // Apply ALL valid promotions and calculate combined discount
    const baseAmount = (selectedRoom.basePricePerNight || 0) * nights
    let totalDiscount = 0
    let remainingAmount = baseAmount

    // Calculate discount for each promotion and apply them sequentially
    // Percentage discounts are applied to the remaining amount after previous discounts
    // Fixed amounts and free nights are applied directly
    const promotionsWithDiscounts = validPromotions.map((promo: any) => {
      let discount = 0
      if (promo.discountType === 'PERCENTAGE') {
        // Apply percentage to remaining amount
        discount = remainingAmount * (parseFloat(promo.discountValue.toString()) / 100)
        remainingAmount = remainingAmount - discount
      } else if (promo.discountType === 'FIXED_AMOUNT') {
        discount = parseFloat(promo.discountValue.toString())
        remainingAmount = Math.max(0, remainingAmount - discount)
      } else if (promo.discountType === 'FREE_NIGHT') {
        const freeNights = parseInt(promo.discountValue.toString())
        discount = (selectedRoom.basePricePerNight || 0) * freeNights
        remainingAmount = Math.max(0, remainingAmount - discount)
      }

      totalDiscount += discount
      return { ...promo, calculatedDiscount: discount }
    })

    // Apply all promotions
    if (promotionsWithDiscounts.length > 0) {
      // Store the first promotion ID (schema only supports one, but we apply all discounts)
      setSelectedPromotion(promotionsWithDiscounts[0]) // Keep first for display
      setAppliedPromotions(promotionsWithDiscounts) // Store all applied promotions
      setBookingForm(prev => ({ ...prev, promotionId: promotionsWithDiscounts[0].id }))
    } else {
      setSelectedPromotion(null)
      setAppliedPromotions([])
      setBookingForm(prev => ({ ...prev, promotionId: '' }))
    }
  }

  // Auto-apply promotion when dates change or promotions are loaded
  useEffect(() => {
    if (selectedRoom && bookingForm.checkInDate && bookingForm.checkOutDate && promotions.length > 0) {
      autoApplyPromotion(bookingForm.checkInDate, bookingForm.checkOutDate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingForm.checkInDate, bookingForm.checkOutDate, promotions.length, selectedRoom?.id])

  // Handle Book Now click (similar to accommodation.tsx)
  const handleBookNow = async (room: any) => {
    setSelectedRoom(room)
    setBookingForm({
      checkInDate: '',
      checkOutDate: '',
      numberOfGuests: '1',
      specialRequests: '',
      promotionId: '',
    })
    setSelectedPromotion(null)
    setAppliedPromotions([])
    setShowBookingDialog(true)

    // Load promotions for this company when opening booking dialog
    const companyId = room.companyId || room.company?.id
    if (companyId) {
      await loadPromotions(companyId)
      // Auto-apply promotions if dates are already selected
      if (bookingForm.checkInDate && bookingForm.checkOutDate) {
        setTimeout(() => {
          autoApplyPromotion(bookingForm.checkInDate, bookingForm.checkOutDate)
        }, 100)
      }
    } else {
      setPromotions([])
    }

    // Try to find or create guest for this company
    await findOrCreateGuest(room.companyId || room.company?.id)
  }

  // Load promotions for overview (only for active companies)
  const loadOverviewPromotions = async () => {
    setLoadingOverviewPromotions(true)
    try {
      // Get active companies from available rooms
      // Extract unique company IDs from available rooms
      const activeCompanyIds = new Set<string>()
      availableRooms.forEach((room: any) => {
        if (room.companyId) {
          activeCompanyIds.add(room.companyId)
        } else if (room.company?.id) {
          activeCompanyIds.add(room.company.id)
        }
      })

      if (activeCompanyIds.size === 0) {
        // If no rooms loaded yet, try to get companies from the accommodations endpoint
        const tenantId = getTenantId()
        const url = `${apiService['baseUrl']}/api/public/accommodations?tenantId=${tenantId}`
        const response = await fetch(url)

        if (response.ok) {
          const data = await response.json()
          if (data.success && data.data?.companies) {
            data.data.companies.forEach((company: any) => {
              if (company.id) {
                activeCompanyIds.add(company.id)
              }
            })
          }
        }
      }

      if (activeCompanyIds.size === 0) {
        setOverviewPromotions([])
        return
      }

      // Load promotions for each active company
      const allPromotionsPromises = Array.from(activeCompanyIds).map(async (companyId) => {
        try {
          return await apiService.getPromotions({
            isActive: true,
            companyId
          })
        } catch (error) {
          console.error(`[MY-ACCOUNT] Failed to load promotions for company ${companyId}:`, error)
          return []
        }
      })

      const promotionsArrays = await Promise.all(allPromotionsPromises)
      const allPromotions = promotionsArrays.flat()

      // Filter promotions that are currently valid (between validFrom and validTo)
      const now = new Date()
      const activePromotions = allPromotions.filter((promo: any) => {
        const validFrom = new Date(promo.validFrom)
        const validTo = new Date(promo.validTo)
        return now >= validFrom && now <= validTo && promo.isActive
      })

      // Remove duplicates (in case same promotion appears for multiple companies)
      const uniquePromotions = activePromotions.filter((promo: any, index: number, self: any[]) =>
        index === self.findIndex((p: any) => p.id === promo.id)
      )

      setOverviewPromotions(uniquePromotions)
    } catch (error) {
      console.error('[MY-ACCOUNT] ❌ Failed to load overview promotions:', error)
      setOverviewPromotions([])
    } finally {
      setLoadingOverviewPromotions(false)
    }
  }

  // Find or create guest for logged-in user (similar to accommodation.tsx)
  const findOrCreateGuest = async (companyId: string): Promise<string | null> => {
    if (!user || !companyId) return null

    try {
      // Try to find existing guest by searching with email
      const guests = await apiService.getHotelGuests({ companyId, search: user.email || '' })
      const existingGuest = Array.isArray(guests)
        ? guests.find((g: any) => g.email === user.email)
        : null

      if (existingGuest) {
        setGuestId(existingGuest.id)
        return existingGuest.id
      }

      // Create new guest from user data
      // Ensure firstName and lastName are valid (required fields)
      const firstName = (user.firstName || user.email?.split('@')[0] || 'Guest').trim()
      const lastName = (user.lastName || '').trim() || 'User' // Ensure lastName is not empty

      if (!firstName || firstName.length === 0) {
        throw new Error('First name is required to create guest profile')
      }

      console.log('Creating new guest with:', { companyId, firstName, lastName, email: user.email })

      // Only send required fields and valid optional fields (not empty strings)
      const guestData: any = {
        companyId,
        firstName,
        lastName,
      }

      // Add optional fields only if they have valid values
      if (user.email && user.email.trim()) {
        guestData.email = user.email.trim()
      }

      // Include phone number if available
      if (user.phone && user.phone.trim()) {
        guestData.phone = user.phone.trim()
      }

      // Include other user details if available
      if (user.country && user.country.trim()) {
        guestData.country = user.country.trim()
      }

      if (user.passportNumber && user.passportNumber.trim()) {
        guestData.passportNumber = user.passportNumber.trim()
      }

      if (user.idNumber && user.idNumber.trim()) {
        guestData.idNumber = user.idNumber.trim()
      }

      const newGuest = await apiService.createHotelGuest(guestData)

      console.log('Guest created successfully:', newGuest)
      setGuestId(newGuest.id)
      return newGuest.id
    } catch (error: any) {
      console.error('Failed to find/create guest:', error)
      toast({
        title: 'Error',
        description: error.message || 'Failed to set up guest profile. Please try again.',
        variant: 'destructive',
      })
      return null
    }
  }

  // Handle booking submission (similar to accommodation.tsx)
  const handleBookingSubmit = async () => {
    if (!selectedRoom) {
      toast({
        title: 'Error',
        description: 'Please select a room',
        variant: 'destructive',
      })
      return
    }

    if (!bookingForm.checkInDate || !bookingForm.checkOutDate) {
      toast({
        title: 'Validation Error',
        description: 'Please select check-in and check-out dates',
        variant: 'destructive',
      })
      return
    }

    setIsCreatingReservation(true)
    try {
      // Ensure guest exists before creating reservation
      const companyId = selectedRoom.companyId || selectedRoom.company?.id
      if (!companyId) {
        throw new Error('Company ID is missing')
      }

      // If guestId is not set, try to find or create guest
      let currentGuestId = guestId
      if (!currentGuestId) {
        currentGuestId = await findOrCreateGuest(companyId)

        if (!currentGuestId) {
          throw new Error('Failed to set up guest profile. Please try again.')
        }
      }

      const checkInDate = new Date(bookingForm.checkInDate).toISOString()
      const checkOutDate = new Date(bookingForm.checkOutDate).toISOString()
      const numberOfGuests = parseInt(bookingForm.numberOfGuests) || 1

      // First, check room availability for the selected dates
      const availabilityResult = await apiService.checkRoomAvailability({
        companyId,
        checkInDate: bookingForm.checkInDate,
        checkOutDate: bookingForm.checkOutDate,
        roomTypeId: selectedRoom.roomTypeId,
        numberOfGuests,
      })

      console.log('=== AVAILABILITY CHECK DEBUG ===')
      console.log('Request params:', {
        companyId,
        checkInDate: bookingForm.checkInDate,
        checkOutDate: bookingForm.checkOutDate,
        roomTypeId: selectedRoom.roomTypeId,
        numberOfGuests,
      })
      console.log('Full availability result:', JSON.stringify(availabilityResult, null, 2))
      console.log('Selected room details:', JSON.stringify(selectedRoom, null, 2))
      console.log('Selected room ID:', selectedRoom.id)
      console.log('Selected room type ID:', selectedRoom.roomTypeId)
      console.log('Available rooms count:', availabilityResult.rooms?.length || 0)
      console.log('Available room IDs:', availabilityResult.rooms?.map((r: any) => r.id) || [])
      console.log('Available room numbers:', availabilityResult.rooms?.map((r: any) => r.roomNumber) || [])
      console.log('=== END DEBUG ===')

      // Check if the selected room is still available
      // If rooms array is empty, no rooms available
      if (!availabilityResult.rooms || availabilityResult.rooms.length === 0) {
        console.error('❌ No rooms available at all')
        toast({
          title: 'Room No Longer Available',
          description: `No rooms of this type are available for the dates ${bookingForm.checkInDate} to ${bookingForm.checkOutDate}. Please select different dates or another room.`,
          variant: 'destructive',
        })
        setIsCreatingReservation(false)
        return
      }

      // Check if the specific room is in the available list
      const isRoomAvailable = availabilityResult.rooms.some((room: any) => {
        const match = room.id === selectedRoom.id
        console.log(`Comparing room ${room.id} (${room.roomNumber}) with selected ${selectedRoom.id} (${selectedRoom.roomNumber}): ${match}`)
        return match
      })

      console.log('Is room available?', isRoomAvailable)

      if (!isRoomAvailable) {
        console.warn('⚠️ Selected room not available, but other rooms of same type are available')
        // Room not available, but other rooms of same type are - use the first available one
        const alternativeRoom = availabilityResult.rooms[0]
        console.log('Using alternative room:', alternativeRoom.roomNumber, alternativeRoom.id)
        
        // Update selectedRoom to use the available alternative
        selectedRoom.id = alternativeRoom.id
        selectedRoom.roomNumber = alternativeRoom.roomNumber
        
        toast({
          title: 'Room Assigned',
          description: `Room ${selectedRoom.roomNumber} has been assigned for your booking (same room type).`,
          variant: 'default',
        })
      }

      console.log('✅ Proceeding with booking for room:', selectedRoom.roomNumber, selectedRoom.id)

      // Calculate base total amount (price per night * number of nights)
      const nights = Math.ceil((new Date(checkOutDate).getTime() - new Date(checkInDate).getTime()) / (1000 * 60 * 60 * 24))
      let baseAmount = (selectedRoom.basePricePerNight || 0) * nights
      let totalAmount = baseAmount
      let discountAmount = 0

      // Apply all promotion discounts
      if (appliedPromotions.length > 0) {
        let remainingAmount = baseAmount
        appliedPromotions.forEach((promo: any) => {
          let promoDiscount = 0
          if (promo.discountType === 'PERCENTAGE') {
            // Apply percentage to remaining amount
            promoDiscount = remainingAmount * (parseFloat(promo.discountValue.toString()) / 100)
            remainingAmount = remainingAmount - promoDiscount
          } else if (promo.discountType === 'FIXED_AMOUNT') {
            promoDiscount = parseFloat(promo.discountValue.toString())
            remainingAmount = Math.max(0, remainingAmount - promoDiscount)
          } else if (promo.discountType === 'FREE_NIGHT') {
            const freeNights = parseInt(promo.discountValue.toString())
            promoDiscount = (selectedRoom.basePricePerNight || 0) * freeNights
            remainingAmount = Math.max(0, remainingAmount - promoDiscount)
          }
          discountAmount += promoDiscount
        })
        totalAmount = remainingAmount
      }

      console.log('Creating reservation with:', {
        companyId,
        roomId: selectedRoom.id,
        guestId: currentGuestId,
        checkInDate,
        checkOutDate,
        numberOfGuests,
        baseAmount,
        discountAmount,
        totalAmount,
        promotionId: bookingForm.promotionId || undefined,
        appliedPromotionIds: appliedPromotions.map((p: any) => p.id),
      })

      const reservation = await apiService.createHotelReservation({
        companyId,
        roomId: selectedRoom.id,
        guestId: currentGuestId,
        checkInDate,
        checkOutDate,
        numberOfGuests,
        totalAmount,
        appliedPromotionIds: appliedPromotions.map((p: any) => p.id),
        depositAmount: 0,
        specialRequests: bookingForm.specialRequests || undefined,
        promotionId: bookingForm.promotionId || undefined,
      })

      console.log('Reservation created successfully:', reservation)

      toast({
        title: 'Success',
        description: 'Reservation created successfully! Your booking has been confirmed.',
      })

      // Close dialog and reset form
      setShowBookingDialog(false)
      setSelectedRoom(null)
      setBookingForm({
        checkInDate: '',
        checkOutDate: '',
        numberOfGuests: '1',
        specialRequests: '',
        promotionId: '',
      })
      setSelectedPromotion(null)

      // Reload bookings and available rooms
      await loadBookings()
      await loadAvailableRooms()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create reservation',
        variant: 'destructive',
      })
    } finally {
      setIsCreatingReservation(false)
    }
  }


  // View booking details
  const handleViewBooking = (booking: any) => {
    setSelectedBooking(booking)
    setShowBookingDetailsDialog(true)
  }

  // Handle edit booking (open edit dialog)
  const handleEditBooking = (booking: any) => {
    if (booking.status !== 'PENDING') {
      toast({
        title: 'Cannot Edit',
        description: 'Only pending reservations can be edited.',
        variant: 'destructive',
      })
      return
    }

    setSelectedBooking(booking)
    // Initialize edit form with current booking data
    setEditBookingForm({
      checkInDate: booking.checkInDate ? new Date(booking.checkInDate).toISOString().split('T')[0] : '',
      checkOutDate: booking.checkOutDate ? new Date(booking.checkOutDate).toISOString().split('T')[0] : '',
      numberOfGuests: booking.numberOfGuests?.toString() || '1',
      specialRequests: booking.specialRequests || '',
    })
    setEditBookingErrors({})
    setShowBookingDetailsDialog(false)
    setShowEditBookingDialog(true)
  }

  // Handle update booking
  const handleUpdateBooking = async () => {
    if (!selectedBooking) return

    // Validate form
    const errors: Record<string, string> = {}
    if (!editBookingForm.checkInDate) {
      errors.checkInDate = 'Check-in date is required'
    }
    if (!editBookingForm.checkOutDate) {
      errors.checkOutDate = 'Check-out date is required'
    }
    if (editBookingForm.checkOutDate <= editBookingForm.checkInDate) {
      errors.checkOutDate = 'Check-out date must be after check-in date'
    }
    if (!editBookingForm.numberOfGuests || parseInt(editBookingForm.numberOfGuests) < 1) {
      errors.numberOfGuests = 'Number of guests must be at least 1'
    }

    if (Object.keys(errors).length > 0) {
      setEditBookingErrors(errors)
      return
    }

    setIsUpdatingBooking(true)
    try {
      const numberOfGuests = parseInt(editBookingForm.numberOfGuests) || 1

      // Check room availability for new dates if dates actually changed
      // Compare dates as strings (YYYY-MM-DD) to ensure accurate comparison
      const originalCheckIn = new Date(selectedBooking.checkInDate).toISOString().split('T')[0]
      const originalCheckOut = new Date(selectedBooking.checkOutDate).toISOString().split('T')[0]
      const newCheckIn = editBookingForm.checkInDate
      const newCheckOut = editBookingForm.checkOutDate

      const datesChanged = newCheckIn !== originalCheckIn || newCheckOut !== originalCheckOut

      // Only check availability if dates actually changed
      if (datesChanged) {
        const availabilityResult = await apiService.checkRoomAvailability({
          companyId: selectedBooking.companyId,
          checkInDate: editBookingForm.checkInDate,
          checkOutDate: editBookingForm.checkOutDate,
          roomTypeId: selectedBooking.room?.roomTypeId,
          numberOfGuests,
        })

        // Check if the room is still available (excluding current reservation)
        const isRoomAvailable = availabilityResult.rooms.some((room: any) =>
          room.id === selectedBooking.roomId
        )

        if (!isRoomAvailable || availabilityResult.rooms.length === 0) {
          toast({
            title: 'Room No Longer Available',
            description: `The room is no longer available for the selected dates. Please choose different dates.`,
            variant: 'destructive',
          })
          setIsUpdatingBooking(false)
          return
        }
      }

      // Update reservation
      // Convert dates to ISO strings only when sending to API
      const checkInDate = new Date(editBookingForm.checkInDate).toISOString()
      const checkOutDate = new Date(editBookingForm.checkOutDate).toISOString()

      await apiService.updateHotelReservation(selectedBooking.id, {
        checkInDate,
        checkOutDate,
        numberOfGuests,
        specialRequests: editBookingForm.specialRequests || undefined,
      })

      toast({
        title: 'Success',
        description: 'Reservation updated successfully!',
      })

      // Close dialog and reset
      setShowEditBookingDialog(false)
      setSelectedBooking(null)
      setEditBookingForm({
        checkInDate: '',
        checkOutDate: '',
        numberOfGuests: '1',
        specialRequests: '',
      })
      setEditBookingErrors({})

      // Reload bookings
      await loadBookings()
      await loadAvailableRooms()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update reservation',
        variant: 'destructive',
      })
    } finally {
      setIsUpdatingBooking(false)
    }
  }

  // Handle cancel reservation (from details dialog)
  const handleCancelReservation = async () => {
    if (!selectedBooking) return

    setIsCancelling(true)
    try {
      await apiService.cancelReservation(selectedBooking.id, cancelReason || undefined)

      toast({
        title: 'Success',
        description: 'Reservation cancelled successfully',
      })

      setShowCancelDialog(false)
      setShowBookingDetailsDialog(false)
      setCancelReason('')
      setSelectedBooking(null)

      // Reload bookings
      await loadBookings()
      await loadAvailableRooms()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to cancel reservation',
        variant: 'destructive',
      })
    } finally {
      setIsCancelling(false)
    }
  }

  // Handle cancel reservation directly from list (for pending bookings)
  const handleCancelReservationFromList = async (booking: any) => {
    if (!confirm('Are you sure you want to cancel this reservation? This action cannot be undone.')) {
      return
    }

    setIsCancelling(true)
    try {
      await apiService.cancelReservation(booking.id)

      toast({
        title: 'Success',
        description: 'Reservation cancelled successfully',
      })

      // Reload bookings
      await loadBookings()
      await loadAvailableRooms()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to cancel reservation',
        variant: 'destructive',
      })
    } finally {
      setIsCancelling(false)
    }
  }

  const handleLogout = async () => {
    setIsLoading(true)
    try {
      await logout()
      // Navigation is handled by auth-context logout
    } catch (error) {
      console.error("Logout failed:", error)
    } finally {
      setIsLoading(false)
    }
  }

  // Get booking status badge
  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { variant: any; label: string }> = {
      PENDING: { variant: 'secondary', label: 'Pending' },
      CONFIRMED: { variant: 'default', label: 'Confirmed' },
      CHECKED_IN: { variant: 'default', label: 'Checked In' },
      CHECKED_OUT: { variant: 'outline', label: 'Completed' },
      CANCELLED: { variant: 'destructive', label: 'Cancelled' },
    }

    const config = statusConfig[status] || { variant: 'outline', label: status }
    return <Badge variant={config.variant}>{config.label}</Badge>
  }

  // Helper function to display role
  const getDisplayRole = (role: string | undefined): string => {
    if (!role) return 'GUEST'
    if (role === 'super_admin') return 'SUPER ADMIN'
    if (role === 'admin') return 'ADMIN'
    return role.toUpperCase()
  }

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin':
        return 'destructive'
      case 'accountant':
        return 'destructive' // Use same variant as admin since it displays as admin
      case 'auditor':
        return 'secondary'
      case 'employee':
        return 'outline'
      case 'guest':
        return 'secondary'
      default:
        return 'outline'
    }
  }

  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Full system access with administrative privileges'
      case 'accountant':
        return 'Access to accounting and financial features'
      case 'auditor':
        return 'Read-only access for auditing purposes'
      case 'employee':
        return 'Standard employee access'
      case 'guest':
        return 'Limited access - please contact administrator for full access'
      default:
        return 'Unknown role'
    }
  }

  if (!user) {
    return null // Will redirect in useEffect
  }

  const isGuest = (user as any).role === 'guest'

  return (
    <div className="min-h-screen" style={{
      background: `linear-gradient(135deg, ${BRAND_COLORS.accent} 0%, #f5f0e8 100%)`,
    }}>
      <style>{`
        .tab-navigation::-webkit-scrollbar {
          display: none;
        }
        /* Ensure Sheet overlay and content are above everything */
        [data-radix-dialog-overlay],
        [data-state="open"][role="dialog"] > div:first-child {
          z-index: 9998 !important;
        }
        [data-radix-dialog-content],
        [data-state="open"][role="dialog"] > div:last-child {
          z-index: 9999 !important;
        }
        /* Target Sheet specifically */
        body > [data-radix-portal] [data-radix-dialog-overlay] {
          z-index: 9998 !important;
        }
        body > [data-radix-portal] [data-radix-dialog-content] {
          z-index: 9999 !important;
        }
        /* Ensure toast appears above dialogs */
        [data-sonner-toaster],
        [data-sonner-toast],
        [data-radix-toast-viewport],
        [data-radix-toast] {
          z-index: 10000 !important;
        }
      `}</style>
      {/* Fixed Messages Toggle Button - Outside Container, Right Side, Bottom */}
      <Button
        onClick={() => setShowMessagesSidebar(!showMessagesSidebar)}
        className="fixed right-8 bottom-28 z-50 shadow-2xl rounded-full h-16 w-16 p-0 hover:scale-110 transition-transform border-4 border-white"
        style={{
          backgroundColor: '#6467f2',
          boxShadow: '0 10px 25px rgba(100, 103, 242, 0.4)'
        }}
        size="lg"
        title={showMessagesSidebar ? 'Hide Messages' : 'Show Messages'}
      >
        <MessageSquare className="h-7 w-7 text-white" />
        {unreadMessageCount > 0 && (
          <Badge className="absolute -top-1 -right-1 h-6 w-6 p-0 flex items-center justify-center bg-red-500 text-white text-xs border-2 border-white font-bold">
            {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
          </Badge>
        )}
      </Button>
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Premium Header with Gradient */}
        <div className="relative overflow-hidden rounded-2xl" style={{
          background: `linear-gradient(135deg, ${BRAND_COLORS.primary} 0%, ${BRAND_COLORS.secondary} 50%, ${BRAND_COLORS.primary} 100%)`,
          zIndex: 1,
        }}>
          {/* Decorative Pattern Overlay */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)`,
            }} />
          </div>

          {/* Animated Gradient Overlay */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.4) 0%, transparent 60%)',
              animation: 'pulse 4s ease-in-out infinite',
            }} />
          </div>

          <div className="relative px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 overflow-x-auto">
            <div className="flex items-center justify-between gap-4 min-w-0">
              {/* Logo Section - More Visible */}
              <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0 relative z-0">
                <Link to="/" className="flex items-center">
                  <img
                    src="/brand-b.png"
                    alt="Ikoro House"
                    className="h-12 w-12 sm:h-20 sm:w-20 md:h-24 md:w-24 lg:h-28 lg:w-28 object-contain"
                  />
                </Link>
              </div>

              {/* Desktop Navigation - Hidden on Mobile */}
              <div className="hidden lg:flex flex-wrap items-center gap-3 min-w-0 flex-shrink">
                <Button
                  onClick={() => navigate("/")}
                  className="border border-white/30 transition-all rounded-xl px-5 py-3 h-auto"
                  style={{
                    background: `linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)`,
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Home className="h-4 w-4 text-white" />
                    <p className="text-sm text-white font-semibold">Back to Home</p>
                  </div>
                </Button>
              </div>

              {/* User Info Section - Desktop Only */}
              <div className="hidden lg:flex items-center gap-4 ml-auto">
                <div className="text-right">
                  <h1 className="text-3xl font-bold text-white mb-1" style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}>
                    {user.firstName} {user.lastName}
                  </h1>
                  <div className="flex items-center justify-end gap-2">
                    <Badge variant="secondary" className="bg-white/20 text-white border-white/30 backdrop-blur-sm">
                      <Shield className="h-3 w-3 mr-1" />
                      {getDisplayRole(user.role)}
                    </Badge>
                    <p className="text-sm text-white/90 font-medium">
                      Guest Portal
                    </p>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="relative cursor-pointer focus:outline-none">
                      <div className="p-4 rounded-2xl transition-all hover:scale-105" style={{
                        background: `linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.2) 100%)`,
                        backdropFilter: 'blur(10px)',
                        border: '2px solid rgba(255,255,255,0.3)',
                      }}>
                        <User className="h-8 w-8 text-white" />
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-green-500 border-2 border-white"></div>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-10 z-50">
                    <DropdownMenuItem
                      onClick={handleLogout}
                      variant="destructive"
                      className="cursor-pointer"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Mobile Menu Toggle - Visible on Mobile */}
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="lg:hidden text-white hover:bg-white/20 p-2 rounded-lg relative z-20"
                    style={{
                      background: `linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)`,
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.3)',
                    }}
                  >
                    <Menu className="h-6 w-6 text-white" />
                    <span className="sr-only">Open navigation menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="right"
                  className="w-80 p-0 !z-[9999]"
                  style={{ zIndex: 9999 }}
                >
                  <div className="flex flex-col h-full">
                    <div className="p-4 border-b border-border">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <img
                            src="/brand-b.png"
                            alt="Ikoro House"
                            className="h-12 w-12 object-contain"
                          />
                          <div>
                            <h2 className="text-lg font-bold text-foreground">Ikoro House</h2>
                            <p className="text-xs text-muted-foreground">Guest Portal</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="h-8 w-8 p-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {/* User Info */}
                      <div className="pb-4 border-b border-border">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 sm:p-3 rounded-xl flex-shrink-0" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                            <User className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: BRAND_COLORS.primary }} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-foreground text-sm sm:text-base truncate">{user.firstName} {user.lastName}</h3>
                            <p className="text-xs sm:text-sm text-muted-foreground break-words overflow-wrap-anywhere">{user.email}</p>
                          </div>
                        </div>
                        <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                          <Shield className="h-3 w-3 mr-1" />
                          {getDisplayRole(user.role)}
                        </Badge>
                      </div>

                      {/* Navigation Tabs */}
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2">Navigation</p>
                        <button
                          onClick={() => {
                            setActiveTab('overview')
                            setIsMobileMenuOpen(false)
                          }}
                          className={`w-full flex items-center gap-3 rounded-lg px-4 py-3 font-medium transition-all ${activeTab === 'overview'
                            ? 'bg-primary text-primary-foreground'
                            : 'text-foreground'
                            }`}
                          style={activeTab === 'overview' ? { backgroundColor: BRAND_COLORS.primary, color: 'white' } : {}}
                          onMouseEnter={(e) => {
                            if (activeTab !== 'overview') {
                              e.currentTarget.style.backgroundColor = BRAND_COLORS.accent;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (activeTab !== 'overview') {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          <Hotel className="h-5 w-5" />
                          <span>Overview</span>
                        </button>
                        <button
                          onClick={() => {
                            setActiveTab('bookings')
                            setIsMobileMenuOpen(false)
                          }}
                          className={`w-full flex items-center gap-3 rounded-lg px-4 py-3 font-medium transition-all ${activeTab === 'bookings'
                            ? 'bg-primary text-primary-foreground'
                            : 'text-foreground'
                            }`}
                          style={activeTab === 'bookings' ? { backgroundColor: BRAND_COLORS.primary, color: 'white' } : {}}
                          onMouseEnter={(e) => {
                            if (activeTab !== 'bookings') {
                              e.currentTarget.style.backgroundColor = BRAND_COLORS.accent;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (activeTab !== 'bookings') {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          <Bed className="h-5 w-5" />
                          <span>My Bookings</span>
                        </button>
                        <button
                          onClick={() => {
                            setActiveTab('new-booking')
                            setIsMobileMenuOpen(false)
                          }}
                          className={`w-full flex items-center gap-3 rounded-lg px-4 py-3 font-medium transition-all ${activeTab === 'new-booking'
                            ? 'bg-primary text-primary-foreground'
                            : 'text-foreground'
                            }`}
                          style={activeTab === 'new-booking' ? { backgroundColor: BRAND_COLORS.primary, color: 'white' } : {}}
                          onMouseEnter={(e) => {
                            if (activeTab !== 'new-booking') {
                              e.currentTarget.style.backgroundColor = BRAND_COLORS.accent;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (activeTab !== 'new-booking') {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          <Plus className="h-5 w-5" />
                          <span>New Booking</span>
                        </button>
                        <button
                          onClick={() => {
                            setActiveTab('statistics')
                            setIsMobileMenuOpen(false)
                          }}
                          className={`w-full flex items-center gap-3 rounded-lg px-4 py-3 font-medium transition-all ${activeTab === 'statistics'
                            ? 'bg-primary text-primary-foreground'
                            : 'text-foreground'
                            }`}
                          style={activeTab === 'statistics' ? { backgroundColor: BRAND_COLORS.primary, color: 'white' } : {}}
                          onMouseEnter={(e) => {
                            if (activeTab !== 'statistics') {
                              e.currentTarget.style.backgroundColor = BRAND_COLORS.accent;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (activeTab !== 'statistics') {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          <BarChart3 className="h-5 w-5" />
                          <span>Statistics</span>
                        </button>
                        <button
                          onClick={() => {
                            setActiveTab('reviews')
                            setIsMobileMenuOpen(false)
                          }}
                          className={`w-full flex items-center gap-3 rounded-lg px-4 py-3 font-medium transition-all ${activeTab === 'reviews'
                            ? 'bg-primary text-primary-foreground'
                            : 'text-foreground'
                            }`}
                          style={activeTab === 'reviews' ? { backgroundColor: BRAND_COLORS.primary, color: 'white' } : {}}
                          onMouseEnter={(e) => {
                            if (activeTab !== 'reviews') {
                              e.currentTarget.style.backgroundColor = BRAND_COLORS.accent;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (activeTab !== 'reviews') {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          <Star className="h-5 w-5" />
                          <span>Reviews</span>
                        </button>
                        <button
                          onClick={() => {
                            setActiveTab('account')
                            setIsMobileMenuOpen(false)
                          }}
                          className={`w-full flex items-center gap-3 rounded-lg px-4 py-3 font-medium transition-all ${activeTab === 'account'
                            ? 'bg-primary text-primary-foreground'
                            : 'text-foreground'
                            }`}
                          style={activeTab === 'account' ? { backgroundColor: BRAND_COLORS.primary, color: 'white' } : {}}
                          onMouseEnter={(e) => {
                            if (activeTab !== 'account') {
                              e.currentTarget.style.backgroundColor = BRAND_COLORS.accent;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (activeTab !== 'account') {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          <User className="h-5 w-5" />
                          <span>My Account</span>
                        </button>
                      </div>

                      {/* Actions */}
                      <div className="pt-4 border-t border-border space-y-2">
                        <Button
                          onClick={() => {
                            navigate("/")
                            setIsMobileMenuOpen(false)
                          }}
                          variant="outline"
                          className="w-full justify-start"
                        >
                          <Home className="h-4 w-4 mr-2" />
                          Back to Home
                        </Button>
                        <Button
                          onClick={() => {
                            handleLogout()
                            setIsMobileMenuOpen(false)
                          }}
                          variant="destructive"
                          className="w-full justify-start"
                        >
                          <LogOut className="h-4 w-4 mr-2" />
                          Logout
                        </Button>
                      </div>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="w-full">
          {/* Premium Tabs Navigation */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="bg-white rounded-2xl p-2 mb-8 border border-gray-100">
              <div
                className="flex lg:grid lg:grid-cols-6 gap-2 overflow-x-auto -mx-2 px-2 lg:mx-0 lg:px-0 tab-navigation"
                style={{
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              >
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`flex items-center justify-center gap-2 rounded-xl px-3 sm:px-4 py-2 font-semibold transition-all duration-300 whitespace-nowrap flex-shrink-0 min-w-fit ${activeTab === 'overview'
                    ? 'bg-white text-[#c39d76] shadow-sm'
                    : 'text-[#2C2416] hover:bg-gray-50'
                    }`}
                  style={{
                    color: activeTab === 'overview' ? BRAND_COLORS.primary : BRAND_COLORS.dark,
                  }}
                >
                  <Hotel className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm sm:text-base">Overview</span>
                </button>
                <button
                  onClick={() => setActiveTab('bookings')}
                  className={`flex items-center justify-center gap-2 rounded-xl px-3 sm:px-4 py-2 font-semibold transition-all duration-300 whitespace-nowrap flex-shrink-0 min-w-fit ${activeTab === 'bookings'
                    ? 'bg-white text-[#c39d76] shadow-sm'
                    : 'text-[#2C2416] hover:bg-gray-50'
                    }`}
                  style={{
                    color: activeTab === 'bookings' ? BRAND_COLORS.primary : BRAND_COLORS.dark,
                  }}
                >
                  <Bed className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm sm:text-base">My Bookings</span>
                </button>
                <button
                  onClick={() => setActiveTab('new-booking')}
                  className={`flex items-center justify-center gap-2 rounded-xl px-3 sm:px-4 py-2 font-semibold transition-all duration-300 whitespace-nowrap flex-shrink-0 min-w-fit ${activeTab === 'new-booking'
                    ? 'bg-white text-[#c39d76] shadow-sm'
                    : 'text-[#2C2416] hover:bg-gray-50'
                    }`}
                  style={{
                    color: activeTab === 'new-booking' ? BRAND_COLORS.primary : BRAND_COLORS.dark,
                  }}
                >
                  <Plus className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm sm:text-base">New Booking</span>
                </button>
                <button
                  onClick={() => setActiveTab('statistics')}
                  className={`flex items-center justify-center gap-2 rounded-xl px-3 sm:px-4 py-2 font-semibold transition-all duration-300 whitespace-nowrap flex-shrink-0 min-w-fit ${activeTab === 'statistics'
                    ? 'bg-white text-[#c39d76] shadow-sm'
                    : 'text-[#2C2416] hover:bg-gray-50'
                    }`}
                  style={{
                    color: activeTab === 'statistics' ? BRAND_COLORS.primary : BRAND_COLORS.dark,
                  }}
                >
                  <BarChart3 className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm sm:text-base">Statistics</span>
                </button>
                <button
                  onClick={() => setActiveTab('reviews')}
                  className={`flex items-center justify-center gap-2 rounded-xl px-3 sm:px-4 py-2 font-semibold transition-all duration-300 whitespace-nowrap flex-shrink-0 min-w-fit ${activeTab === 'reviews'
                    ? 'bg-white text-[#c39d76] shadow-sm'
                    : 'text-[#2C2416] hover:bg-gray-50'
                    }`}
                  style={{
                    color: activeTab === 'reviews' ? BRAND_COLORS.primary : BRAND_COLORS.dark,
                  }}
                >
                  <Star className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm sm:text-base">Reviews</span>
                </button>
                <button
                  onClick={() => setActiveTab('account')}
                  className={`flex items-center justify-center gap-2 rounded-xl px-3 sm:px-4 py-2 font-semibold transition-all duration-300 whitespace-nowrap flex-shrink-0 min-w-fit ${activeTab === 'account'
                    ? 'bg-white text-[#c39d76] shadow-sm'
                    : 'text-[#2C2416] hover:bg-gray-50'
                    }`}
                  style={{
                    color: activeTab === 'account' ? BRAND_COLORS.primary : BRAND_COLORS.dark,
                  }}
                >
                  <User className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm sm:text-base">My Account</span>
                </button>
              </div>
            </div>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-8 animate-in fade-in-50 duration-300">
              {/* Premium Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="border-0 bg-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-wide mb-2" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Total Bookings</p>
                        <p className="text-4xl font-bold mb-1" style={{ color: BRAND_COLORS.primary }}>
                          {bookings.length}
                        </p>
                        <p className="text-xs" style={{ color: BRAND_COLORS.dark, opacity: 0.6 }}>All time reservations</p>
                      </div>
                      <div className="p-4 rounded-xl" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                        <Hotel className="h-8 w-8" style={{ color: BRAND_COLORS.primary }} />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 bg-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-wide mb-2" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Active Bookings</p>
                        <p className="text-4xl font-bold mb-1" style={{ color: '#10b981' }}>
                          {bookings.filter(b => ['PENDING', 'CONFIRMED', 'CHECKED_IN'].includes(b.status)).length}
                        </p>
                        <p className="text-xs" style={{ color: BRAND_COLORS.dark, opacity: 0.6 }}>Upcoming & current</p>
                      </div>
                      <div className="p-4 rounded-xl" style={{ backgroundColor: '#10b98120' }}>
                        <CheckCircle className="h-8 w-8" style={{ color: '#10b981' }} />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 bg-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-wide mb-2" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Completed Stays</p>
                        <p className="text-4xl font-bold mb-1" style={{ color: '#f59e0b' }}>
                          {bookings.filter(b => b.status === 'CHECKED_OUT').length}
                        </p>
                        <p className="text-xs" style={{ color: BRAND_COLORS.dark, opacity: 0.6 }}>Past reservations</p>
                      </div>
                      <div className="p-4 rounded-xl" style={{ backgroundColor: '#f59e0b20' }}>
                        <Star className="h-8 w-8" style={{ color: '#f59e0b' }} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Bookings - Premium Design */}
              <Card className="border-0">
                <CardHeader className="border-b" style={{
                  background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
                }}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                    <CardTitle className="flex items-center gap-2 sm:gap-3 text-white">
                      <div className="p-1.5 sm:p-2 rounded-lg bg-white/20 backdrop-blur-sm">
                        <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
                      </div>
                      <span className="text-base sm:text-lg">Recent Bookings</span>
                    </CardTitle>
                    <Button
                      onClick={() => setActiveTab('bookings')}
                      variant="ghost"
                      size="sm"
                      className="text-white hover:bg-white/20 text-xs sm:text-sm self-start sm:self-auto"
                    >
                      View All →
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-6 bg-white">
                  {loadingBookings ? (
                    <div className="flex justify-center py-12">
                      <MoonLoader size="lg" color="teal" />
                    </div>
                  ) : bookings.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="inline-flex p-4 rounded-full mb-4" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                        <Bed className="h-12 w-12" style={{ color: BRAND_COLORS.primary }} />
                      </div>
                      <p className="text-lg font-semibold mb-2" style={{ color: BRAND_COLORS.dark }}>
                        No bookings yet
                      </p>
                      <p className="text-sm mb-6" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>
                        Start your journey with us
                      </p>
                      <Button
                        onClick={() => setActiveTab('new-booking')}
                        size="lg"
                        className="transition-all"
                        style={{ backgroundColor: BRAND_COLORS.primary, color: 'white' }}
                      >
                        <Plus className="h-5 w-5 mr-2" />
                        Create First Booking
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3 sm:space-y-4">
                      {bookings.slice(0, 3).map((booking) => (
                        <div
                          key={booking.id}
                          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 p-3 sm:p-5 rounded-xl transition-all duration-300 cursor-pointer border border-gray-100 hover:border-[#c39d76] group"
                          onClick={() => handleViewBooking(booking)}
                        >
                          <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                            <div className="p-2 sm:p-3 rounded-xl group-hover:scale-110 transition-transform flex-shrink-0" style={{
                              background: `linear-gradient(135deg, ${BRAND_COLORS.accent} 0%, ${BRAND_COLORS.secondary} 100%)`,
                            }}>
                              <Bed className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: BRAND_COLORS.primary }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-sm sm:text-base mb-1 truncate" style={{ color: BRAND_COLORS.dark }}>
                                Room {booking.room?.roomNumber || 'N/A'}
                              </p>
                              <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>
                                <span className="flex items-center gap-1.5">
                                  <Calendar className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                                  <span className="whitespace-nowrap">
                                    {new Date(booking.checkInDate).toLocaleDateString()} - {new Date(booking.checkOutDate).toLocaleDateString()}
                                  </span>
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                            <div className="hidden sm:block">
                              {getStatusBadge(booking.status)}
                            </div>
                            <div className="sm:hidden">
                              {getStatusBadge(booking.status)}
                            </div>
                            {booking.status === 'PENDING' && (
                              <div className="flex gap-2">
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleEditBooking(booking)
                                  }}
                                  size="sm"
                                  className="shadow-md hover:shadow-lg transition-all text-xs sm:text-sm px-2 sm:px-3 h-7 sm:h-8"
                                  style={{
                                    backgroundColor: BRAND_COLORS.primary,
                                    color: 'white',
                                  }}
                                >
                                  <Edit className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                                  <span className="hidden sm:inline">Edit</span>
                                  <span className="sm:hidden">E</span>
                                </Button>
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleCancelReservationFromList(booking)
                                  }}
                                  variant="destructive"
                                  size="sm"
                                  className="shadow-md hover:shadow-lg transition-all text-xs sm:text-sm px-2 sm:px-3 h-7 sm:h-8"
                                  disabled={isCancelling}
                                >
                                  <X className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                                  <span className="hidden sm:inline">Cancel</span>
                                  <span className="sm:hidden">X</span>
                                </Button>
                              </div>
                            )}
                            <div className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 transition-colors">
                              <Eye className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: BRAND_COLORS.primary }} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Active Promotions */}
              <Card className="border-0">
                <CardHeader className="border-b" style={{
                  background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
                }}>
                  <CardTitle className="flex items-center gap-2 sm:gap-3 text-white">
                    <div className="p-1.5 sm:p-2 rounded-lg bg-white/20 backdrop-blur-sm flex-shrink-0">
                      <Gift className="h-4 w-4 sm:h-5 sm:w-5" />
                    </div>
                    <span className="text-base sm:text-lg break-words">Active Promotions</span>
                  </CardTitle>
                  <CardDescription className="text-white/90 mt-2 text-xs sm:text-sm">
                    Special offers and discounts available now
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6 bg-white">
                  {loadingOverviewPromotions ? (
                    <div className="flex justify-center py-12">
                      <MoonLoader size="lg" color="teal" />
                    </div>
                  ) : overviewPromotions.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="inline-flex p-4 rounded-full mb-4" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                        <Gift className="h-12 w-12" style={{ color: BRAND_COLORS.primary }} />
                      </div>
                      <p className="text-lg font-semibold mb-2" style={{ color: BRAND_COLORS.dark }}>
                        No active promotions
                      </p>
                      <p className="text-sm" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>
                        Check back later for special offers
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {overviewPromotions.map((promo) => (
                        <div
                          key={promo.id}
                          className="border rounded-lg p-4 hover:border-[#c39d76] transition-colors"
                          style={{
                            background: `linear-gradient(135deg, rgba(195,157,118,0.1) 0%, rgba(212,165,116,0.05) 100%)`,
                          }}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-0 mb-3">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <div className="p-1.5 sm:p-2 rounded-lg flex-shrink-0" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                                <Tag className="h-3 w-3 sm:h-4 sm:w-4" style={{ color: BRAND_COLORS.primary }} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h3 className="font-semibold text-sm sm:text-base break-words" style={{ color: BRAND_COLORS.dark }}>
                                  {promo.name}
                                </h3>
                                {promo.code && (
                                  <p className="text-xs font-mono break-words truncate" style={{ color: BRAND_COLORS.primary }}>
                                    Code: {promo.code}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Badge className="bg-green-100 text-green-800 border-green-300 text-xs flex-shrink-0 self-start sm:self-auto">
                              <Percent className="h-3 w-3 mr-1" />
                              <span className="whitespace-nowrap">
                                {promo.discountType === 'PERCENTAGE'
                                  ? `${promo.discountValue}% OFF`
                                  : promo.discountType === 'FIXED_AMOUNT'
                                    ? `${formatCurrency(promo.discountValue, 'FRW')} OFF`
                                    : 'Special Offer'}
                              </span>
                            </Badge>
                          </div>
                          {promo.description && (
                            <p className="text-xs sm:text-sm mb-3 break-words" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>
                              {promo.description}
                            </p>
                          )}
                          <div className="flex items-center gap-2 text-xs" style={{ color: BRAND_COLORS.dark, opacity: 0.6 }}>
                            <Calendar className="h-3 w-3" />
                            <span>
                              Valid until {new Date(promo.validTo).toLocaleDateString()}
                            </span>
                          </div>
                          {promo.minStay && (
                            <p className="text-xs mt-2" style={{ color: BRAND_COLORS.dark, opacity: 0.6 }}>
                              Minimum {promo.minStay} night{promo.minStay > 1 ? 's' : ''} stay
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions - Compact Cards */}
              <Card className="border-0">
                <CardHeader className="border-b bg-white pb-3">
                  <CardTitle className="text-sm sm:text-base md:text-lg font-semibold break-words" style={{ color: BRAND_COLORS.dark }}>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="pt-4 bg-white">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Button
                      onClick={() => setActiveTab('new-booking')}
                      className="h-auto py-3 px-4 flex items-center gap-3 rounded-lg transition-colors"
                      style={{
                        background: `linear-gradient(135deg, ${BRAND_COLORS.primary} 0%, ${BRAND_COLORS.secondary} 100%)`,
                        color: 'white',
                      }}
                    >
                      <div className="p-1.5 rounded bg-white/20">
                        <Plus className="h-4 w-4" />
                      </div>
                      <div className="text-left flex-1">
                        <div className="font-semibold text-sm">New Booking</div>
                        <div className="text-xs opacity-90">Reserve a room</div>
                      </div>
                    </Button>

                    <Button
                      onClick={() => setActiveTab('bookings')}
                      variant="outline"
                      className="h-auto py-3 px-4 flex items-center gap-3 rounded-lg border transition-colors"
                      style={{
                        borderColor: BRAND_COLORS.primary,
                        color: BRAND_COLORS.dark,
                        backgroundColor: 'white',
                      }}
                    >
                      <div className="p-1.5 rounded" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                        <Bed className="h-4 w-4" style={{ color: BRAND_COLORS.primary }} />
                      </div>
                      <div className="text-left flex-1">
                        <div className="font-semibold text-sm">My Bookings</div>
                        <div className="text-xs opacity-70">View all reservations</div>
                      </div>
                    </Button>

                    <Button
                      onClick={() => setActiveTab('account')}
                      variant="outline"
                      className="h-auto py-3 px-4 flex items-center gap-3 rounded-lg border transition-colors"
                      style={{
                        borderColor: BRAND_COLORS.primary,
                        color: BRAND_COLORS.dark,
                        backgroundColor: 'white',
                      }}
                    >
                      <div className="p-1.5 rounded" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                        <User className="h-4 w-4" style={{ color: BRAND_COLORS.primary }} />
                      </div>
                      <div className="text-left flex-1">
                        <div className="font-semibold text-sm">My Account</div>
                        <div className="text-xs opacity-70">Manage profile</div>
                      </div>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Bookings Tab */}
            <TabsContent value="bookings" className="space-y-6 animate-in fade-in-50 duration-300">
              <Card className="border-0 shadow-xl">
                <CardHeader className="border-b" style={{
                  background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
                }}>
                  <CardTitle className="flex items-center gap-2 sm:gap-3 text-white">
                    <div className="p-1.5 sm:p-2 rounded-lg bg-white/20 backdrop-blur-sm">
                      <Hotel className="h-4 w-4 sm:h-5 sm:w-5" />
                    </div>
                    <span className="text-base sm:text-lg">My Bookings</span>
                  </CardTitle>
                  <CardDescription className="text-white/90 mt-2 text-xs sm:text-sm">
                    View and manage your hotel reservations
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-4 sm:pt-6 bg-white">
                  {/* Filters */}
                  <div className="mb-4 sm:mb-6 space-y-3 sm:space-y-4 p-3 sm:p-4 bg-gray-50 rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <Filter className="h-4 w-4" style={{ color: BRAND_COLORS.primary }} />
                      <span className="font-semibold text-xs sm:text-sm" style={{ color: BRAND_COLORS.dark }}>Filters</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                      <div>
                        <Label className="text-xs mb-1" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Status</Label>
                        <Select
                          value={bookingFilters.status}
                          onValueChange={(value) => setBookingFilters({ ...bookingFilters, status: value })}
                        >
                          <SelectTrigger className="h-9 sm:h-10 text-xs sm:text-sm">
                            <SelectValue placeholder="All Statuses" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Statuses</SelectItem>
                            <SelectItem value="PENDING">Pending</SelectItem>
                            <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                            <SelectItem value="CHECKED_IN">Checked In</SelectItem>
                            <SelectItem value="CHECKED_OUT">Completed</SelectItem>
                            <SelectItem value="CANCELLED">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs mb-1" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Search</Label>
                        <div className="relative">
                          <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                          <Input
                            placeholder="Room, reservation number..."
                            value={bookingFilters.search}
                            onChange={(e) => setBookingFilters({ ...bookingFilters, search: e.target.value })}
                            className="pl-7 sm:pl-8 h-9 sm:h-10 text-xs sm:text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs mb-1" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>From Date</Label>
                        <Input
                          type="date"
                          value={bookingFilters.dateFrom}
                          onChange={(e) => setBookingFilters({ ...bookingFilters, dateFrom: e.target.value })}
                          className="h-9 sm:h-10 text-xs sm:text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs mb-1" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>To Date</Label>
                        <Input
                          type="date"
                          value={bookingFilters.dateTo}
                          onChange={(e) => setBookingFilters({ ...bookingFilters, dateTo: e.target.value })}
                          className="h-9 sm:h-10 text-xs sm:text-sm"
                        />
                      </div>
                    </div>
                    {(bookingFilters.status !== 'all' || bookingFilters.search || bookingFilters.dateFrom || bookingFilters.dateTo) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setBookingFilters({ status: 'all', search: '', dateFrom: '', dateTo: '' })}
                        className="mt-2 text-xs sm:text-sm h-8 sm:h-9"
                      >
                        <X className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                        Clear Filters
                      </Button>
                    )}
                  </div>
                  {loadingBookings ? (
                    <div className="flex justify-center py-12">
                      <MoonLoader size="lg" color="teal" />
                    </div>
                  ) : bookings.length === 0 ? (
                    <div className="text-center py-12">
                      <Hotel className="h-16 w-16 mx-auto mb-4" style={{ color: BRAND_COLORS.primary, opacity: 0.5 }} />
                      <p className="text-lg font-medium mb-2" style={{ color: BRAND_COLORS.dark }}>No bookings yet</p>
                      <p className="text-sm mb-4" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>
                        Start by creating your first reservation
                      </p>
                      <Button
                        onClick={() => setActiveTab('new-booking')}
                        style={{ backgroundColor: BRAND_COLORS.primary, color: 'white' }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        New Booking
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2 sm:space-y-3">
                      {bookings.map((booking) => (
                        <Card key={booking.id} className="border shadow-sm">
                          <CardContent className="p-3 sm:p-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                              <div className="flex-1 flex items-center gap-2 sm:gap-3 min-w-0">
                                <div className="p-1.5 sm:p-2 rounded-lg flex-shrink-0" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                                  <Bed className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: BRAND_COLORS.primary }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <p className="font-semibold text-sm sm:text-base truncate" style={{ color: BRAND_COLORS.dark }}>
                                      Room {booking.room?.roomNumber || 'N/A'}
                                    </p>
                                    <div className="flex-shrink-0">
                                      {getStatusBadge(booking.status)}
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1.5 text-xs sm:text-sm" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>
                                    <span className="flex items-center gap-1 whitespace-nowrap">
                                      <Calendar className="h-3 w-3 flex-shrink-0" />
                                      <span className="truncate">{new Date(booking.checkInDate).toLocaleDateString()} - {new Date(booking.checkOutDate).toLocaleDateString()}</span>
                                    </span>
                                    <span className="flex items-center gap-1 whitespace-nowrap">
                                      <Users className="h-3 w-3 flex-shrink-0" />
                                      {booking.numberOfGuests} {booking.numberOfGuests === 1 ? 'Guest' : 'Guests'}
                                    </span>
                                    {booking.totalAmount && (
                                      <span className="flex items-center gap-1 font-semibold whitespace-nowrap" style={{ color: BRAND_COLORS.primary }}>
                                        <DollarSign className="h-3 w-3 flex-shrink-0" />
                                        {formatCurrency(booking.totalAmount, booking.room?.currency || 'FRW')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="flex gap-2 sm:flex-shrink-0">
                                {booking.status === 'PENDING' && (
                                  <>
                                    <Button
                                      onClick={() => handleEditBooking(booking)}
                                      size="sm"
                                      className="h-7 sm:h-8 text-xs sm:text-sm px-2 sm:px-3"
                                      style={{
                                        backgroundColor: BRAND_COLORS.primary,
                                        color: 'white',
                                      }}
                                    >
                                      <Edit className="h-3 w-3 mr-1" />
                                      <span className="hidden sm:inline">Edit</span>
                                      <span className="sm:hidden">E</span>
                                    </Button>
                                    <Button
                                      onClick={() => handleCancelReservationFromList(booking)}
                                      variant="destructive"
                                      size="sm"
                                      className="h-7 sm:h-8 text-xs sm:text-sm px-2 sm:px-3"
                                      disabled={isCancelling}
                                    >
                                      <X className="h-3 w-3 mr-1" />
                                      <span className="hidden sm:inline">Cancel</span>
                                      <span className="sm:hidden">X</span>
                                    </Button>
                                  </>
                                )}
                                {(booking.status === 'CHECKED_OUT' || booking.status === 'CONFIRMED') && !hasReview(booking.id) && (
                                  <Button
                                    onClick={() => handleOpenReviewDialog(booking)}
                                    size="sm"
                                    className="h-7 sm:h-8 text-xs sm:text-sm px-2 sm:px-3"
                                    style={{ backgroundColor: BRAND_COLORS.primary, color: 'white' }}
                                  >
                                    <Star className="h-3 w-3 mr-1" />
                                    <span className="hidden sm:inline">Review</span>
                                    <span className="sm:hidden">★</span>
                                  </Button>
                                )}
                                <Button
                                  onClick={() => handleViewBooking(booking)}
                                  variant="outline"
                                  size="sm"
                                  className="h-7 sm:h-8 text-xs sm:text-sm px-2 sm:px-3"
                                  style={{ borderColor: BRAND_COLORS.primary, color: BRAND_COLORS.dark }}
                                >
                                  <Eye className="h-3 w-3 mr-1" />
                                  <span className="hidden sm:inline">Details</span>
                                  <span className="sm:hidden">View</span>
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}

                  {/* Pagination */}
                  {bookingsTotal > bookingsPageSize && (
                    <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                      <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                        Showing {(bookingsPage - 1) * bookingsPageSize + 1} to {Math.min(bookingsPage * bookingsPageSize, bookingsTotal)} of {bookingsTotal} bookings
                      </div>
                      <Pagination>
                        <PaginationContent>
                          <PaginationItem>
                            <PaginationPrevious
                              onClick={() => setBookingsPage(p => Math.max(1, p - 1))}
                              className={bookingsPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                            />
                          </PaginationItem>
                          {Array.from({ length: Math.ceil(bookingsTotal / bookingsPageSize) }, (_, i) => i + 1)
                            .filter(page => {
                              // Show first page, last page, current page, and pages around current
                              return page === 1 ||
                                page === Math.ceil(bookingsTotal / bookingsPageSize) ||
                                Math.abs(page - bookingsPage) <= 1
                            })
                            .map((page, idx, arr) => {
                              // Add ellipsis if there's a gap
                              const showEllipsisBefore = idx > 0 && page - arr[idx - 1] > 1
                              return (
                                <React.Fragment key={page}>
                                  {showEllipsisBefore && (
                                    <PaginationItem>
                                      <PaginationEllipsis />
                                    </PaginationItem>
                                  )}
                                  <PaginationItem>
                                    <PaginationLink
                                      onClick={() => setBookingsPage(page)}
                                      isActive={bookingsPage === page}
                                      className="cursor-pointer"
                                    >
                                      {page}
                                    </PaginationLink>
                                  </PaginationItem>
                                </React.Fragment>
                              )
                            })}
                          <PaginationItem>
                            <PaginationNext
                              onClick={() => setBookingsPage(p => Math.min(Math.ceil(bookingsTotal / bookingsPageSize), p + 1))}
                              className={bookingsPage >= Math.ceil(bookingsTotal / bookingsPageSize) ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                            />
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* New Booking Tab */}
            <TabsContent value="new-booking" className="space-y-6 animate-in fade-in-50 duration-300">
              <Card className="border-0 shadow-xl">
                <CardHeader className="border-b" style={{
                  background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
                }}>
                  <CardTitle className="flex items-center gap-2 sm:gap-3 text-white">
                    <div className="p-1.5 sm:p-2 rounded-lg bg-white/20 backdrop-blur-sm">
                      <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                    </div>
                    <span className="text-base sm:text-lg">Create New Booking</span>
                  </CardTitle>
                  <CardDescription className="text-white/90 mt-2 text-xs sm:text-sm">
                    Reserve a room for your stay
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-4 sm:pt-6 bg-white">
                  <div className="space-y-4 sm:space-y-6">
                    {/* Company Tabs */}
                    {companies.length > 0 && (
                      <div className="mb-2">
                        <Label className="text-base font-semibold mb-3 block" style={{ color: BRAND_COLORS.dark }}>Select Company</Label>
                        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                          <button
                            onClick={() => setRoomFilters({ ...roomFilters, companyId: 'all' })}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${roomFilters.companyId === 'all'
                              ? 'shadow-md transform scale-105'
                              : 'hover:bg-gray-100'
                              }`}
                            style={{
                              backgroundColor: roomFilters.companyId === 'all' ? BRAND_COLORS.primary : 'white',
                              color: roomFilters.companyId === 'all' ? 'white' : BRAND_COLORS.dark,
                              border: `1px solid ${roomFilters.companyId === 'all' ? BRAND_COLORS.primary : '#e5e7eb'}`
                            }}
                          >
                            All Departments
                          </button>
                          {companies.map((company) => (
                            <button
                              key={company.id}
                              onClick={() => setRoomFilters({ ...roomFilters, companyId: company.id })}
                              className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${roomFilters.companyId === company.id
                                ? 'shadow-md transform scale-105'
                                : 'hover:bg-gray-100'
                                }`}
                              style={{
                                backgroundColor: roomFilters.companyId === company.id ? BRAND_COLORS.primary : 'white',
                                color: roomFilters.companyId === company.id ? 'white' : BRAND_COLORS.dark,
                                border: `1px solid ${roomFilters.companyId === company.id ? BRAND_COLORS.primary : '#e5e7eb'}`
                              }}
                            >
                              {company.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Filters */}
                    <div className="space-y-3 sm:space-y-4 p-3 sm:p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center gap-2 mb-2">
                        <Filter className="h-4 w-4" style={{ color: BRAND_COLORS.primary }} />
                        <span className="font-semibold text-xs sm:text-sm" style={{ color: BRAND_COLORS.dark }}>Filter Rooms</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        <div>
                          <Label className="text-xs mb-1" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Room Type</Label>
                          <Select
                            value={roomFilters.roomType}
                            onValueChange={(value) => setRoomFilters({ ...roomFilters, roomType: value })}
                          >
                            <SelectTrigger className="h-9 sm:h-10 text-xs sm:text-sm">
                              <SelectValue placeholder="All Types" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Types</SelectItem>
                              {roomTypes.map((type) => (
                                <SelectItem key={type.id} value={type.id}>
                                  {type.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs mb-1" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Search</Label>
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                            <Input
                              placeholder="Room number, name..."
                              value={roomFilters.search}
                              onChange={(e) => setRoomFilters({ ...roomFilters, search: e.target.value })}
                              className="pl-7 sm:pl-8 h-9 sm:h-10 text-xs sm:text-sm"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs mb-1" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Min Price (FRW)</Label>
                          <Input
                            type="number"
                            placeholder="Min"
                            value={roomFilters.minPrice}
                            onChange={(e) => setRoomFilters({ ...roomFilters, minPrice: e.target.value })}
                            className="h-9 sm:h-10 text-xs sm:text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs mb-1" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Max Price (FRW)</Label>
                          <Input
                            type="number"
                            placeholder="Max"
                            value={roomFilters.maxPrice}
                            onChange={(e) => setRoomFilters({ ...roomFilters, maxPrice: e.target.value })}
                            className="h-9 sm:h-10 text-xs sm:text-sm"
                          />
                        </div>
                      </div>
                      {(roomFilters.roomType !== 'all' || roomFilters.search || roomFilters.minPrice || roomFilters.maxPrice) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRoomFilters({ companyId: 'all', roomType: 'all', search: '', minPrice: '', maxPrice: '' })}
                          className="mt-2 text-xs sm:text-sm h-8 sm:h-9"
                        >
                          <X className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                          Clear Filters
                        </Button>
                      )}
                    </div>

                    {/* Available Rooms Display - Same format as accommodation.tsx */}
                    <div className="space-y-6">
                      {availableRooms.length === 0 ? (
                        <div className="text-center py-12 border-2 border-dashed rounded-lg" style={{ borderColor: BRAND_COLORS.primary }}>
                          <Bed className="h-12 w-12 mx-auto mb-4" style={{ color: BRAND_COLORS.primary, opacity: 0.5 }} />
                          <p className="text-sm font-medium" style={{ color: BRAND_COLORS.dark }}>
                            No rooms available at this time
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                            {availableRooms.slice(0, roomsToShow).map((room: any) => {
                              const mainImage = getRoomImage(room);

                              return (
                                <div
                                  key={room.id}
                                  onClick={() => {
                                    setSelectedRoom(room);
                                    setCurrentImageIndex(0);
                                    setShowRoomDetailsModal(true);
                                  }}
                                  className="cursor-pointer group"
                                >
                                  {/* Minimal Room Card - Image with Info Overlay at Bottom */}
                                  <div className="relative h-[340px] sm:h-[380px] rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                                    {/* Room Image */}
                                    <img
                                      src={mainImage}
                                      alt={room.title || room.roomNumber}
                                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).src = '/images/webflow/outside.jpg';
                                      }}
                                    />

                                    {/* Reserved Badge */}
                                    {isRoomReserved(room) && (
                                      <div className="absolute top-3 right-3 z-10">
                                        <span className="bg-red-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-lg">
                                          Reserved
                                        </span>
                                      </div>
                                    )}

                                    {/* Gradient Overlay at Bottom */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                                    {/* Room Info - Minimal: name, company, and price */}
                                    <div className="absolute bottom-0 left-0 right-0 p-4 space-y-2">
                                      <h3 className="font-semibold text-lg text-white truncate">
                                        {room.roomNumber || room.title}
                                      </h3>

                                      {room.company?.name && (
                                        <p className="text-white/90 text-sm truncate">
                                          {room.company.name}
                                        </p>
                                      )}

                                      {/* Price */}
                                      <div className="pt-1">
                                        <span className="font-semibold text-xl text-white">
                                          {formatCurrency(room.basePricePerNight, room.currency || 'RWF')}
                                        </span>
                                        <span className="text-white/90 text-sm"> / night</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Load More Button */}
                          {availableRooms.length > roomsToShow && (
                            <div className="flex justify-center mt-8">
                              <Button
                                onClick={() => setRoomsToShow(prev => prev + 8)}
                                className="bg-[#c39d76] hover:bg-[#b38a66] text-white px-8 py-3 text-base font-semibold transition-colors"
                              >
                                Load More Rooms
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Room Details Modal */}
            <Dialog open={showRoomDetailsModal} onOpenChange={setShowRoomDetailsModal}>
              <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto p-0">
                {selectedRoom && (
                  <div className="bg-white">
                    {/* Close Button */}
                    <button
                      onClick={() => setShowRoomDetailsModal(false)}
                      className="absolute top-4 right-4 z-50 bg-white/80 hover:bg-white p-2 rounded-full transition-all"
                    >
                      <X size={20} />
                    </button>

                    {/* Image Gallery */}
                    <div className="p-4 sm:p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Main Image */}
                        <div 
                          className="relative h-[300px] md:h-[400px] rounded-xl overflow-hidden cursor-pointer group"
                          onClick={() => selectedRoom.images && selectedRoom.images.length > 0 && setShowImageGallery(true)}
                        >
                          {selectedRoom.images && selectedRoom.images.length > 0 ? (
                            <>
                              <img
                                src={getImageUrl(selectedRoom.images[currentImageIndex])}
                                alt={selectedRoom.title || selectedRoom.roomNumber}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                onError={(e) => {
                                  console.error('🖼️  [MY-ACCOUNT] ❌ Failed to load main room image:', getImageUrl(selectedRoom.images[currentImageIndex]));
                                  // Remove the failed image from selectedRoom state
                                  setSelectedRoom((prev: any) => {
                                    if (!prev || !prev.images) return prev;
                                    const updatedImages = prev.images.filter((img: any, idx: number) => idx !== currentImageIndex);
                                    // Adjust currentImageIndex if needed
                                    if (currentImageIndex >= updatedImages.length && updatedImages.length > 0) {
                                      setCurrentImageIndex(0);
                                    }
                                    return { ...prev, images: updatedImages };
                                  });
                                }}
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                  View all {selectedRoom.images.length} photos
                                </span>
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                              <span className="text-gray-400">No image available</span>
                            </div>
                          )}

                          {/* Navigation Arrows */}
                          {selectedRoom.images && selectedRoom.images.length > 1 && (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full transition-all"
                              >
                                <ChevronLeft size={20} />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full transition-all"
                              >
                                <ChevronRight size={20} />
                              </button>
                            </>
                          )}
                        </div>

                        {/* Thumbnail Grid */}
                        <div className="grid grid-cols-2 gap-2">
                          {selectedRoom.images && selectedRoom.images.length > 1 && selectedRoom.images.slice(1, 5).map((image: any, index: number) => (
                            <div
                              key={image.id}
                              className="relative h-[145px] md:h-[195px] rounded-xl overflow-hidden cursor-pointer group"
                              onClick={() => {
                                setCurrentImageIndex(index + 1);
                                setShowImageGallery(true);
                              }}
                            >
                              <img
                                src={getImageUrl(image)}
                                alt={`${selectedRoom.roomNumber} - ${index + 2}`}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                onError={(e) => {
                                  console.error('🖼️  [MY-ACCOUNT] ❌ Failed to load thumbnail image:', getImageUrl(image));
                                  // Remove the failed image from selectedRoom state
                                  setSelectedRoom((prev: any) => {
                                    if (!prev || !prev.images) return prev;
                                    const updatedImages = prev.images.filter((img: any) => img.id !== image.id);
                                    return { ...prev, images: updatedImages };
                                  });
                                }}
                              />
                              {index === 3 && selectedRoom.images.length > 5 && (
                                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                  <span className="text-white text-xl font-bold">
                                    +{selectedRoom.images.length - 5} more
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Room Details */}
                    <div className="p-4 sm:p-6">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Left Column - Room Info */}
                        <div className="lg:col-span-2 space-y-6">
                          {/* Title and Basic Info */}
                          <div>
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                              {selectedRoom.title || selectedRoom.roomTypeRef?.name || `Room ${selectedRoom.roomNumber}`}
                            </h2>
                            <p className="text-lg text-gray-600 mb-3">Room {selectedRoom.roomNumber}</p>
                            
                            {/* Company and Reservation Status */}
                            <div className="flex flex-wrap items-center gap-4">
                              {/* Company */}
                              {selectedRoom.company && (
                                <div className="flex items-center gap-2 text-gray-700">
                                  <MapPin className="text-[#c39d76]" size={16} />
                                  <span className="font-medium">{selectedRoom.company.name}</span>
                                </div>
                              )}
                              
                              {/* Reservation Status */}
                              {selectedRoom.reservations && selectedRoom.reservations.length > 0 && (
                                <div>
                                  {selectedRoom.reservations.some((r: any) => r.status !== 'CANCELLED' && r.status !== 'CHECKED_OUT') ? (
                                    <span className="inline-flex items-center gap-1.5 bg-red-100 text-red-800 text-sm font-semibold px-3 py-1 rounded-full">
                                      <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                      Reserved
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-800 text-sm font-semibold px-3 py-1 rounded-full">
                                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                      Available
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              {/* Show Available if no reservations */}
                              {(!selectedRoom.reservations || selectedRoom.reservations.length === 0) && (
                                <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-800 text-sm font-semibold px-3 py-1 rounded-full">
                                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                  Available
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Quick Stats */}
                          <div className="flex flex-wrap gap-4 py-4 border-y border-gray-200">
                            {(selectedRoom.capacity || selectedRoom.maxOccupancy || selectedRoom.roomTypeRef?.capacity) && (
                              <div className="flex items-center gap-2">
                                <Users className="text-gray-600" size={18} />
                                <span className="text-gray-700 text-sm">
                                  {selectedRoom.capacity || selectedRoom.maxOccupancy || selectedRoom.roomTypeRef?.capacity} guests
                                </span>
                              </div>
                            )}
                            {(selectedRoom.bedType || selectedRoom.roomTypeRef?.bedType) && (
                              <div className="flex items-center gap-2">
                                <Bed className="text-gray-600" size={18} />
                                <span className="text-gray-700 text-sm">{selectedRoom.bedType || selectedRoom.roomTypeRef?.bedType}</span>
                              </div>
                            )}
                          </div>

                          {/* Description */}
                          {(selectedRoom.description || selectedRoom.roomTypeRef?.description) && (
                            <div>
                              <h3 className="text-xl font-bold text-gray-900 mb-3">About this room</h3>
                              {(() => {
                                const description = selectedRoom.description || selectedRoom.roomTypeRef?.description || '';
                                const descriptionItems = description.split(/[;,]/).map((item: string) => item.trim()).filter((item: string) => item.length > 0);
                                
                                if (descriptionItems.length === 1) {
                                  return (
                                    <p className="text-gray-700 leading-relaxed text-sm">
                                      {descriptionItems[0]}
                                    </p>
                                  );
                                }
                                
                                return (
                                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-gray-700 leading-relaxed list-none text-sm">
                                    {descriptionItems.map((item: string, index: number) => (
                                      <li key={index} className="flex items-start">
                                        <span className="mr-2 text-[#c39d76] font-bold flex-shrink-0">•</span>
                                        <span>{item}</span>
                                      </li>
                                    ))}
                                  </ul>
                                );
                              })()}
                            </div>
                          )}

                          {/* Amenities */}
                          {selectedRoom.amenities && selectedRoom.amenities.length > 0 && (
                            <div>
                              <h3 className="text-xl font-bold text-gray-900 mb-3">Amenities</h3>
                              <div className="grid grid-cols-2 gap-3">
                                {selectedRoom.amenities.map((item: any) => (
                                  <div key={item.id} className="flex items-center gap-2">
                                    <CheckCircle className="text-green-600" size={16} />
                                    <span className="text-gray-700 text-sm">{item.amenity.name}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Right Column - Booking Card */}
                        <div className="lg:col-span-1">
                          <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                            <div className="mb-4 flex items-baseline gap-2">
                              <div className="text-2xl font-bold text-gray-900">
                                {formatCurrency(selectedRoom.basePricePerNight, selectedRoom.currency)}
                              </div>
                              <div className="text-gray-600 text-sm">per night</div>
                            </div>

                            {/* Availability Calendar - Read Only */}
                            <div className="mb-4">
                              <RoomAvailabilityView
                                roomId={selectedRoom.id}
                                monthsToShow={1}
                                variant="inline"
                              />
                            </div>

                            {/* Book Now Button */}
                            <Button
                              onClick={() => {
                                setShowRoomDetailsModal(false);
                                handleBookNow(selectedRoom);
                              }}
                              className="w-full py-4 text-base font-bold bg-[#c39d76] hover:bg-[#b38a66] text-white transition-colors"
                            >
                              Book Now
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Full Screen Image Gallery Modal */}
            {showImageGallery && selectedRoom && (
              <div className="fixed inset-0 bg-black z-[9999] flex items-center justify-center">
                <button
                  onClick={() => setShowImageGallery(false)}
                  className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
                >
                  <X size={32} />
                </button>

                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all z-10"
                >
                  <ChevronLeft size={32} />
                </button>

                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all z-10"
                >
                  <ChevronRight size={32} />
                </button>

                <img
                  src={getImageUrl(selectedRoom.images[currentImageIndex])}
                  alt={`${selectedRoom.roomNumber} - ${currentImageIndex + 1}`}
                  className="max-w-full max-h-full object-contain"
                />

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-full">
                  {currentImageIndex + 1} / {selectedRoom.images.length}
                </div>
              </div>
            )}

            {/* Statistics Tab */}
            <TabsContent value="statistics" className="space-y-8 animate-in fade-in-50 duration-300">
              {/* Statistics Graphs */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Monthly Bookings Trend */}
                <Card className="border-0 bg-white">
                  <CardHeader className="border-b bg-white pb-3">
                    <CardTitle className="flex items-center gap-2 text-sm sm:text-base md:text-lg font-semibold" style={{ color: BRAND_COLORS.dark }}>
                      <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" style={{ color: BRAND_COLORS.primary }} />
                      <span className="break-words">Booking Trends (Last 6 Months)</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    {allBookings.length === 0 ? (
                      <div className="flex items-center justify-center h-64 text-center">
                        <div>
                          <BarChart3 className="h-12 w-12 mx-auto mb-3" style={{ color: BRAND_COLORS.dark, opacity: 0.3 }} />
                          <p className="text-sm" style={{ color: BRAND_COLORS.dark, opacity: 0.6 }}>
                            No booking data available
                          </p>
                        </div>
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={getMonthlyBookingsData()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis
                            dataKey="month"
                            stroke={BRAND_COLORS.dark}
                            style={{ fontSize: '10px' }}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                          />
                          <YAxis
                            stroke={BRAND_COLORS.dark}
                            style={{ fontSize: '10px' }}
                            width={40}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'white',
                              border: `1px solid ${BRAND_COLORS.primary}`,
                              borderRadius: '8px',
                            }}
                          />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="bookings"
                            stroke={BRAND_COLORS.primary}
                            strokeWidth={2}
                            name="Total Bookings"
                            dot={{ fill: BRAND_COLORS.primary, r: 4 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="confirmed"
                            stroke="#10b981"
                            strokeWidth={2}
                            name="Confirmed"
                            dot={{ fill: '#10b981', r: 4 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="completed"
                            stroke="#8b5cf6"
                            strokeWidth={2}
                            name="Completed"
                            dot={{ fill: '#8b5cf6', r: 4 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>

                {/* Booking Status Distribution */}
                <Card className="border-0 bg-white">
                  <CardHeader className="border-b bg-white pb-3">
                    <CardTitle className="flex items-center gap-2 text-sm sm:text-base md:text-lg font-semibold" style={{ color: BRAND_COLORS.dark }}>
                      <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" style={{ color: BRAND_COLORS.primary }} />
                      <span className="break-words">Booking Status Distribution</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    {allBookings.length === 0 ? (
                      <div className="flex items-center justify-center h-64 text-center">
                        <div>
                          <BarChart3 className="h-12 w-12 mx-auto mb-3" style={{ color: BRAND_COLORS.dark, opacity: 0.3 }} />
                          <p className="text-sm" style={{ color: BRAND_COLORS.dark, opacity: 0.6 }}>
                            No booking data available
                          </p>
                        </div>
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={getStatusDistributionData()}
                            cx="50%"
                            cy="50%"
                            label={(entry: any) => {
                              const { cx, cy, midAngle, innerRadius, outerRadius, name, percent } = entry
                              const RADIAN = Math.PI / 180
                              // Position labels outside the pie chart for better visibility
                              const radius = outerRadius + 20
                              const x = cx + radius * Math.cos(-midAngle * RADIAN)
                              const y = cy + radius * Math.sin(-midAngle * RADIAN)

                              // Use dark color for all labels for better contrast
                              const textColor = BRAND_COLORS.dark

                              return (
                                <text
                                  x={x}
                                  y={y}
                                  fill={textColor}
                                  textAnchor={x > cx ? 'start' : 'end'}
                                  dominantBaseline="central"
                                  fontSize={11}
                                  style={{ fontWeight: 'bold' }}
                                >
                                  {`${name}: ${(percent * 100).toFixed(0)}%`}
                                </text>
                              )
                            }}
                            labelLine={true}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {getStatusDistributionData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'white',
                              border: `1px solid ${BRAND_COLORS.primary}`,
                              borderRadius: '8px',
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Monthly Bookings Bar Chart */}
              <Card className="border-0 bg-white">
                <CardHeader className="border-b bg-white pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm sm:text-base md:text-lg font-semibold" style={{ color: BRAND_COLORS.dark }}>
                    <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" style={{ color: BRAND_COLORS.primary }} />
                    <span className="break-words">Monthly Bookings Overview</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  {allBookings.length === 0 ? (
                    <div className="flex items-center justify-center h-64 text-center">
                      <div>
                        <BarChart3 className="h-12 w-12 mx-auto mb-3" style={{ color: BRAND_COLORS.dark, opacity: 0.3 }} />
                        <p className="text-sm" style={{ color: BRAND_COLORS.dark, opacity: 0.6 }}>
                          No booking data available
                        </p>
                      </div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={getMonthlyBookingsData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                          dataKey="month"
                          stroke={BRAND_COLORS.dark}
                          style={{ fontSize: '10px' }}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis
                          stroke={BRAND_COLORS.dark}
                          style={{ fontSize: '10px' }}
                          width={40}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: `1px solid ${BRAND_COLORS.primary}`,
                            borderRadius: '8px',
                          }}
                        />
                        <Legend />
                        <Bar
                          dataKey="bookings"
                          fill={BRAND_COLORS.primary}
                          name="Total Bookings"
                          radius={[8, 8, 0, 0]}
                        />
                        <Bar
                          dataKey="confirmed"
                          fill="#10b981"
                          name="Confirmed"
                          radius={[8, 8, 0, 0]}
                        />
                        <Bar
                          dataKey="completed"
                          fill="#8b5cf6"
                          name="Completed"
                          radius={[8, 8, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Reviews Tab */}
            <TabsContent value="reviews" className="space-y-6 animate-in fade-in-50 duration-300">
              <Card className="border-0 shadow-xl">
                <CardHeader className="border-b" style={{
                  background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
                }}>
                  <CardTitle className="flex items-center gap-2 sm:gap-3 text-white">
                    <div className="p-1.5 sm:p-2 rounded-lg bg-white/20 backdrop-blur-sm">
                      <Star className="h-4 w-4 sm:h-5 sm:w-5" />
                    </div>
                    <span className="text-base sm:text-lg">My Reviews</span>
                  </CardTitle>
                  <CardDescription className="text-white/90 mt-2 text-xs sm:text-sm">
                    View and manage your hotel reviews
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-4 sm:pt-6 bg-white">
                  {loadingReviews ? (
                    <div className="flex justify-center py-12">
                      <MoonLoader size="lg" color="teal" />
                    </div>
                  ) : reviews.length === 0 ? (
                    <div className="text-center py-12">
                      <Star className="h-16 w-16 mx-auto mb-4" style={{ color: BRAND_COLORS.primary, opacity: 0.5 }} />
                      <p className="text-lg font-medium mb-2" style={{ color: BRAND_COLORS.dark }}>No reviews yet</p>
                      <p className="text-sm mb-4" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>
                        Submit a review for your completed bookings
                      </p>
                      <Button
                        onClick={() => setActiveTab('bookings')}
                        style={{ backgroundColor: BRAND_COLORS.primary, color: 'white' }}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View My Bookings
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {reviews.map((review) => (
                        <Card key={review.id} className="border shadow-sm">
                          <CardContent className="p-4">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <p className="font-semibold text-base" style={{ color: BRAND_COLORS.dark }}>
                                    Room {review.room?.roomNumber || 'N/A'}
                                  </p>
                                  {review.isVerified && (
                                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Verified
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 mb-2">
                                  {[...Array(5)].map((_, i) => (
                                    <Star
                                      key={i}
                                      className={`w-4 h-4 ${i < review.rating
                                        ? 'fill-yellow-400 text-yellow-400'
                                        : 'text-gray-300'
                                        }`}
                                    />
                                  ))}
                                </div>
                                {review.comment && (
                                  <p className="text-sm mb-2" style={{ color: BRAND_COLORS.dark, opacity: 0.8 }}>
                                    {review.comment}
                                  </p>
                                )}
                                <p className="text-xs" style={{ color: BRAND_COLORS.dark, opacity: 0.6 }}>
                                  {new Date(review.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Account Tab */}
            {/* Messages Tab */}
            <TabsContent value="messages" className="space-y-6 animate-in fade-in-50 duration-300">
              <Card className="border-0 shadow-xl">
                <CardHeader className="border-b" style={{
                  background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
                }}>
                  <CardTitle className="flex items-center gap-3 text-white">
                    <div className="p-2 rounded-lg bg-white/20 backdrop-blur-sm">
                      <MessageSquare className="h-5 w-5" />
                    </div>
                    <span>Messages</span>
                  </CardTitle>
                  <CardDescription className="text-white/90 mt-2">
                    Chat with hotel staff about your reservations and inquiries
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6 bg-white">
                  {loadingMessages ? (
                    <div className="flex items-center justify-center py-12">
                      <MoonLoader />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Messages List */}
                      <div className="border rounded-lg p-4 h-[500px] overflow-y-auto bg-gray-50">
                        {messages.length === 0 ? (
                          <div className="flex flex-col items-center justify-center h-full text-center py-12">
                            <MessageSquare className="h-12 w-12 text-gray-400 mb-4" />
                            <p className="text-gray-500 font-medium">No messages yet</p>
                            <p className="text-sm text-gray-400 mt-2">Start a conversation with hotel staff</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {messages.map((message) => (
                              <div
                                key={message.id}
                                className={`flex ${message.isFromGuest ? 'justify-end' : 'justify-start'}`}
                              >
                                <div
                                  className={`max-w-[70%] rounded-lg p-3 ${message.isFromGuest
                                    ? 'bg-primary text-white'
                                    : 'bg-white border border-gray-200 text-gray-900'
                                    }`}
                                  style={
                                    message.isFromGuest
                                      ? { backgroundColor: BRAND_COLORS.primary }
                                      : {}
                                  }
                                >
                                  <p className="text-sm font-medium mb-1">
                                    {message.isFromGuest
                                      ? `${message.guest?.firstName || ''} ${message.guest?.lastName || ''}`
                                      : message.user?.name || 'Hotel Staff'}
                                  </p>
                                  <p className="text-sm whitespace-pre-wrap break-words">
                                    {message.messageText}
                                  </p>
                                  <p className="text-xs mt-2 opacity-70">
                                    {new Date(message.createdAt).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Message Input */}
                      <div className="flex gap-2">
                        <Textarea
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type your message..."
                          className="flex-1 min-h-[80px] resize-none"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault()
                              handleSendMessage()
                            }
                          }}
                          disabled={sendingMessage || !currentGuestId}
                        />
                        <Button
                          onClick={handleSendMessage}
                          disabled={!newMessage.trim() || sendingMessage || !currentGuestId}
                          className="px-6"
                          style={{ backgroundColor: BRAND_COLORS.primary }}
                        >
                          {sendingMessage ? (
                            <MoonLoader size="sm" />
                          ) : (
                            <Send className="h-5 w-5" />
                          )}
                        </Button>
                      </div>
                      {!currentGuestId && (
                        <p className="text-sm text-gray-500 text-center">
                          Please make a booking first to start messaging
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="account" className="space-y-6 animate-in fade-in-50 duration-300">
              {/* Account Information */}
              <Card className="border-0 shadow-xl">
                <CardHeader className="border-b" style={{
                  background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
                }}>
                  <CardTitle className="flex items-center gap-3 text-white">
                    <div className="p-2 rounded-lg bg-white/20 backdrop-blur-sm">
                      <User className="h-5 w-5" />
                    </div>
                    <span>Account Information</span>
                  </CardTitle>
                  <CardDescription className="text-white/90 mt-2">
                    Your personal details and account settings
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6 bg-white">
                  <div className="space-y-6">
                    {/* Personal Information */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold" style={{ color: BRAND_COLORS.dark }}>
                          Personal Information
                        </h3>
                        {!isEditingProfile ? (
                          <Button
                            onClick={handleEditProfile}
                            variant="outline"
                            size="sm"
                            style={{
                              borderColor: BRAND_COLORS.primary,
                              color: BRAND_COLORS.primary,
                            }}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </Button>
                        ) : (
                          <div className="flex gap-2">
                            <Button
                              onClick={handleSaveProfile}
                              disabled={isSavingProfile}
                              size="sm"
                              style={{
                                backgroundColor: BRAND_COLORS.primary,
                                color: 'white',
                              }}
                            >
                              <Save className="h-4 w-4 mr-2" />
                              {isSavingProfile ? 'Saving...' : 'Save'}
                            </Button>
                            <Button
                              onClick={handleCancelEdit}
                              variant="outline"
                              size="sm"
                              disabled={isSavingProfile}
                              style={{
                                borderColor: BRAND_COLORS.dark,
                                color: BRAND_COLORS.dark,
                              }}
                            >
                              <X className="h-4 w-4 mr-2" />
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="space-y-6">
                        {/* First Name and Last Name Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>First Name</label>
                            {isEditingProfile ? (
                              <Input
                                value={profileForm.firstName}
                                onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
                                className="p-4 rounded-xl border-2"
                                style={{ borderColor: BRAND_COLORS.primary }}
                                placeholder="First Name"
                              />
                            ) : (
                              <div className="flex items-center gap-3 p-4 rounded-xl border-2 shadow-sm hover:shadow-md transition-all" style={{
                                backgroundColor: 'white',
                                borderColor: BRAND_COLORS.primary,
                              }}>
                                <div className="p-2 rounded-lg" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                                  <User className="h-5 w-5" style={{ color: BRAND_COLORS.primary }} />
                                </div>
                                <span className="font-semibold" style={{ color: BRAND_COLORS.dark }}>{user.firstName}</span>
                              </div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <label className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Last Name</label>
                            {isEditingProfile ? (
                              <Input
                                value={profileForm.lastName}
                                onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
                                className="p-4 rounded-xl border-2"
                                style={{ borderColor: BRAND_COLORS.primary }}
                                placeholder="Last Name"
                              />
                            ) : (
                              <div className="flex items-center gap-3 p-4 rounded-xl border-2 shadow-sm hover:shadow-md transition-all" style={{
                                backgroundColor: 'white',
                                borderColor: BRAND_COLORS.primary,
                              }}>
                                <div className="p-2 rounded-lg" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                                  <User className="h-5 w-5" style={{ color: BRAND_COLORS.primary }} />
                                </div>
                                <span className="font-semibold" style={{ color: BRAND_COLORS.dark }}>{user.lastName}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Phone Number and Email Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Phone Number</label>
                            {isEditingProfile ? (
                              <Input
                                value={profileForm.phone}
                                onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                                className="p-4 rounded-xl border-2"
                                style={{ borderColor: BRAND_COLORS.primary }}
                                placeholder="Phone Number"
                                type="tel"
                              />
                            ) : (
                              <div className="flex items-center gap-3 p-4 rounded-xl border-2 shadow-sm hover:shadow-md transition-all" style={{
                                backgroundColor: 'white',
                                borderColor: BRAND_COLORS.primary,
                              }}>
                                <div className="p-2 rounded-lg" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                                  <Phone className="h-5 w-5" style={{ color: BRAND_COLORS.primary }} />
                                </div>
                                <span className="font-semibold" style={{ color: BRAND_COLORS.dark }}>
                                  {user.phone || 'Not provided'}
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <label className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Email Address</label>
                            <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl border-2 shadow-sm hover:shadow-md transition-all" style={{
                              backgroundColor: 'white',
                              borderColor: BRAND_COLORS.primary,
                            }}>
                              <div className="p-1.5 sm:p-2 rounded-lg flex-shrink-0" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                                <Mail className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: BRAND_COLORS.primary }} />
                              </div>
                              <div className="flex-1 flex items-center gap-2 min-w-0">
                                <span className="font-semibold text-xs sm:text-sm break-words overflow-wrap-anywhere" style={{ color: BRAND_COLORS.dark }}>{user.email}</span>
                                <div className="flex-shrink-0">
                                  {user.isEmailVerified ? (
                                    <Badge className="bg-green-100 text-green-800 border-green-300 text-xs">
                                      <CheckCircle2 className="h-3 w-3 mr-1" />
                                      <span className="hidden sm:inline">Verified</span>
                                      <span className="sm:hidden">✓</span>
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 text-xs">
                                      <XCircle className="h-3 w-3 mr-1" />
                                      <span className="hidden sm:inline">Unverified</span>
                                      <span className="sm:hidden">!</span>
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            {!user.isEmailVerified && (
                              <div className="mt-2">
                                <Button
                                  onClick={async () => {
                                    try {
                                      await apiService.resendVerificationEmail(user.email)
                                      toast({
                                        title: "Verification email sent",
                                        description: "Please check your email to verify your account.",
                                      })
                                    } catch (error) {
                                      toast({
                                        title: "Failed to send verification email",
                                        description: "Please try again later.",
                                        variant: "destructive",
                                      })
                                    }
                                  }}
                                  variant="outline"
                                  size="sm"
                                  className="text-xs"
                                  style={{
                                    borderColor: BRAND_COLORS.primary,
                                    color: BRAND_COLORS.primary,
                                  }}
                                >
                                  <Mail className="h-3 w-3 mr-1" />
                                  Resend Verification Email
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Country and ID Number Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Country</label>
                            {isEditingProfile ? (
                              <SearchableSelect
                                options={COUNTRIES}
                                value={profileForm.country}
                                onValueChange={(value) => setProfileForm({ ...profileForm, country: value })}
                                placeholder="Select country..."
                                searchPlaceholder="Search countries..."
                                emptyText="No countries found."
                                className="w-full"
                              />
                            ) : (
                              <div className="flex items-center gap-3 p-4 rounded-xl border-2 shadow-sm hover:shadow-md transition-all" style={{
                                backgroundColor: 'white',
                                borderColor: BRAND_COLORS.primary,
                              }}>
                                <div className="p-2 rounded-lg" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                                  <MapPin className="h-5 w-5" style={{ color: BRAND_COLORS.primary }} />
                                </div>
                                <span className="font-semibold" style={{ color: BRAND_COLORS.dark }}>
                                  {(user as any)?.country || 'Not provided'}
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <label className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>National ID / Passport</label>
                            {isEditingProfile ? (
                              <div className="grid grid-cols-2 gap-3">
                                <Select
                                  value={profileForm.idType}
                                  onValueChange={(value) => {
                                    setProfileForm({
                                      ...profileForm,
                                      idType: value,
                                      // Clear the other field when switching types
                                      passportNumber: value === 'passport' ? profileForm.passportNumber : '',
                                      idNumber: value === 'national_id' ? profileForm.idNumber : '',
                                    });
                                  }}
                                >
                                  <SelectTrigger className="w-full" style={{ borderColor: BRAND_COLORS.primary }}>
                                    <SelectValue placeholder="ID Type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="passport">Passport</SelectItem>
                                    <SelectItem value="national_id">National ID</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Input
                                  value={profileForm.idType === 'passport' ? profileForm.passportNumber : profileForm.idNumber}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/[^A-Z0-9\-]/gi, '');
                                    if (profileForm.idType === 'passport') {
                                      setProfileForm({ ...profileForm, passportNumber: value });
                                    } else if (profileForm.idType === 'national_id') {
                                      setProfileForm({ ...profileForm, idNumber: value });
                                    }
                                  }}
                                  placeholder={profileForm.idType === 'passport' ? 'Passport #' : profileForm.idType === 'national_id' ? 'National ID #' : 'Select type'}
                                  maxLength={20}
                                  className="w-full"
                                  style={{ borderColor: BRAND_COLORS.primary }}
                                  disabled={!profileForm.idType}
                                />
                              </div>
                            ) : (
                              <div className="flex items-center gap-3 p-4 rounded-xl border-2 shadow-sm hover:shadow-md transition-all" style={{
                                backgroundColor: 'white',
                                borderColor: BRAND_COLORS.primary,
                              }}>
                                <div className="p-2 rounded-lg" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                                  <FileText className="h-5 w-5" style={{ color: BRAND_COLORS.primary }} />
                                </div>
                                <span className="font-semibold" style={{ color: BRAND_COLORS.dark }}>
                                  {(user as any)?.passportNumber || (user as any)?.idNumber || 'Not provided'}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Account Status */}
                    <div className="border-t pt-8" style={{ borderColor: `${BRAND_COLORS.primary}40` }}>
                      <h3 className="text-xl font-bold mb-6" style={{ color: BRAND_COLORS.dark }}>
                        Account Status
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Status</label>
                          <div className="flex items-center gap-3 p-4 rounded-xl border-2 shadow-sm" style={{
                            backgroundColor: '#f0fdf4',
                            borderColor: '#10b981',
                          }}>
                            <CheckCircle className="h-5 w-5" style={{ color: '#10b981' }} />
                            <span className="font-semibold" style={{ color: '#10b981' }}>Active</span>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Member Since</label>
                          <div className="flex items-center gap-3 p-4 rounded-xl border-2 shadow-sm hover:shadow-md transition-all" style={{
                            backgroundColor: 'white',
                            borderColor: BRAND_COLORS.primary,
                          }}>
                            <div className="p-2 rounded-lg" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                              <Calendar className="h-5 w-5" style={{ color: BRAND_COLORS.primary }} />
                            </div>
                            <span className="font-semibold" style={{ color: BRAND_COLORS.dark }}>
                              {new Date(user.createdAt || Date.now()).toLocaleDateString('en-US', {
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Account Type</label>
                          <div className="flex items-center gap-3 p-4 rounded-xl border-2 shadow-sm hover:shadow-md transition-all" style={{
                            backgroundColor: 'white',
                            borderColor: BRAND_COLORS.primary,
                          }}>
                            <div className="p-2 rounded-lg" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                              <Shield className="h-5 w-5" style={{ color: BRAND_COLORS.primary }} />
                            </div>
                            <Badge variant={getRoleBadgeVariant(user.role || 'guest')} className="text-sm font-semibold px-3 py-1">
                              {getDisplayRole(user.role)}
                            </Badge>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Total Bookings</label>
                          <div className="flex items-center gap-3 p-4 rounded-xl border-2 shadow-sm hover:shadow-md transition-all" style={{
                            backgroundColor: 'white',
                            borderColor: BRAND_COLORS.primary,
                          }}>
                            <div className="p-2 rounded-lg" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                              <Hotel className="h-5 w-5" style={{ color: BRAND_COLORS.primary }} />
                            </div>
                            <span className="font-bold text-lg" style={{ color: BRAND_COLORS.primary }}>{bookings.length}</span>
                            <span className="font-semibold" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Reservations</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Account Actions */}
                    <div className="border-t pt-8" style={{ borderColor: `${BRAND_COLORS.primary}40` }}>
                      <h3 className="text-xl font-bold mb-6" style={{ color: BRAND_COLORS.dark }}>
                        Account Actions
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {!isGuest && (
                          <Button
                            onClick={() => navigate("/dashboard")}
                            variant="outline"
                            className="h-auto py-5 justify-start rounded-xl border-2 hover:shadow-lg transition-all group"
                            style={{ borderColor: BRAND_COLORS.primary, color: BRAND_COLORS.dark, backgroundColor: 'white' }}
                          >
                            <div className="p-2 rounded-lg mr-3 group-hover:scale-110 transition-transform" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                              <Shield className="h-5 w-5" style={{ color: BRAND_COLORS.primary }} />
                            </div>
                            <div className="text-left">
                              <div className="font-bold text-base">Go to Dashboard</div>
                              <div className="text-xs opacity-70">Access admin features</div>
                            </div>
                          </Button>
                        )}

                        <Button
                          onClick={handleLogout}
                          variant="outline"
                          className="w-full md:col-span-2 h-auto py-5 justify-center rounded-xl border-2 hover:shadow-lg transition-all group"
                          style={{ borderColor: '#ef4444', color: '#ef4444', backgroundColor: 'white' }}
                          disabled={isLoading}
                        >
                          <div className="p-2 rounded-lg mr-3 group-hover:scale-110 transition-transform bg-red-50">
                            <AlertTriangle className="h-5 w-5" style={{ color: '#ef4444' }} />
                          </div>
                          <div className="text-center">
                            <div className="font-bold text-base">{isLoading ? "Signing out..." : "Sign Out"}</div>
                            <div className="text-xs opacity-70">End your session</div>
                          </div>
                        </Button>
                      </div>
                    </div>

                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Messages Sidebar - Overlay (Fixed Position) */}
        {showMessagesSidebar && (
          <div className="fixed right-4 top-1/2 -translate-y-1/2 z-[100] w-96 animate-in slide-in-from-right duration-300">
            <Card className="border-0 shadow-2xl">
              <CardHeader className="border-b" style={{
                background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
              }}>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-3 text-white">
                    <div className="p-2 rounded-lg bg-white/20 backdrop-blur-sm">
                      <MessageSquare className="h-5 w-5" />
                    </div>
                    <span>Messages</span>
                  </CardTitle>
                  <Button
                    onClick={() => setShowMessagesSidebar(false)}
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/20 h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <CardDescription className="text-white/90 mt-2">
                  Chat with hotel staff
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 bg-white p-0 flex flex-col" style={{ height: '500px', maxHeight: '80vh' }}>
                {loadingMessages ? (
                  <div className="flex items-center justify-center py-12 flex-1">
                    <MoonLoader />
                  </div>
                ) : (
                  <>
                    {/* Messages List */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                      {messages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center py-12">
                          <MessageSquare className="h-12 w-12 text-gray-400 mb-4" />
                          <p className="text-gray-500 font-medium">No messages yet</p>
                          <p className="text-sm text-gray-400 mt-2">Start a conversation</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {messages.map((message) => {
                            // Extract image URL - check for /api/images/ pattern (MongoDB GridFS) or [IMAGE:...] (legacy base64)
                            let imageUrl: string | null = null
                            let textWithoutImage = message.messageText || ''

                            if (message.messageText) {
                              // First, check for MongoDB GridFS image URL pattern: /api/images/{fileId}
                              const patterns = [
                                /\/api\/images\/([a-fA-F0-9]{24})/,  // Standard format with 24 hex chars
                                /\/api\/images\/([a-fA-F0-9]+)/,     // Any hex length with leading slash
                                /api\/images\/([a-fA-F0-9]{24})/,    // Without leading slash, 24 chars
                                /api\/images\/([a-fA-F0-9]+)/,       // Without leading slash, any length
                              ]

                              let gridFsMatch = null
                              for (const pattern of patterns) {
                                gridFsMatch = message.messageText.match(pattern)
                                if (gridFsMatch) break
                              }

                              if (gridFsMatch) {
                                // Construct full URL
                                let imagePath = gridFsMatch[0]
                                if (!imagePath.startsWith('/')) {
                                  imagePath = '/' + imagePath
                                }

                                // Get baseUrl from apiService
                                const baseUrl = (apiService as any)['baseUrl'] || config.api.baseUrl || (typeof window !== 'undefined' ? window.location.origin : '')
                                imageUrl = imagePath.startsWith('http')
                                  ? imagePath
                                  : imagePath.startsWith('/')
                                    ? `${baseUrl}${imagePath}`
                                    : `${baseUrl}/${imagePath}`

                                // Remove the image URL from text
                                const escapedMatch = gridFsMatch[0].replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                                textWithoutImage = message.messageText
                                  .replace(new RegExp(`\\s*${escapedMatch}\\s*`, 'g'), '')
                                  .replace(new RegExp(`^\\s*${escapedMatch}\\s*$`, 'gm'), '')
                                  .replace(/\n\n+/g, '\n')
                                  .replace(/^\n+|\n+$/g, '')
                                  .trim()
                              } else {
                                // Fallback: Try to extract /api/images/ URL manually
                                if (message.messageText.includes('/api/images/') || message.messageText.includes('api/images/')) {
                                  const manualMatch = message.messageText.match(/(\/api\/images\/[a-fA-F0-9]+|api\/images\/[a-fA-F0-9]+)/)
                                  if (manualMatch) {
                                    let imagePath = manualMatch[0]
                                    if (!imagePath.startsWith('/')) {
                                      imagePath = '/' + imagePath
                                    }

                                    const baseUrl = (apiService as any)['baseUrl'] || config.api.baseUrl || (typeof window !== 'undefined' ? window.location.origin : '')
                                    imageUrl = imagePath.startsWith('http')
                                      ? imagePath
                                      : imagePath.startsWith('/')
                                        ? `${baseUrl}${imagePath}`
                                        : `${baseUrl}/${imagePath}`

                                    const escapedPath = imagePath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                                    textWithoutImage = message.messageText
                                      .replace(new RegExp(`\\s*${escapedPath}\\s*`, 'g'), '')
                                      .replace(new RegExp(`^\\s*${escapedPath}\\s*$`, 'gm'), '')
                                      .replace(/\n\n+/g, '\n')
                                      .replace(/^\n+|\n+$/g, '')
                                      .trim()
                                  }
                                }

                                // Legacy: Try to match [IMAGE:...] for base64 images (backward compatibility)
                                if (!imageUrl) {
                                  const imageMatch = message.messageText.match(/\[IMAGE:([\s\S]*?)\]/)
                                  if (imageMatch && imageMatch[1]) {
                                    const extracted = imageMatch[1].trim()

                                    // Check if it's already a data URL
                                    if (extracted.startsWith('data:image/')) {
                                      imageUrl = extracted
                                    } else if (extracted.startsWith('data:')) {
                                      imageUrl = extracted
                                    } else {
                                      // Assume it's base64 and add data URL prefix
                                      let mimeType = 'image/jpeg' // default
                                      if (extracted.startsWith('/9j/') || extracted.startsWith('iVBORw0KGgo')) {
                                        mimeType = extracted.startsWith('/9j/') ? 'image/jpeg' : 'image/png'
                                      }
                                      imageUrl = `data:${mimeType};base64,${extracted}`
                                    }

                                    // Remove the image tag from text
                                    textWithoutImage = message.messageText.replace(/\[IMAGE:[\s\S]*?\]/, '').trim()
                                  }
                                }
                              }
                            }

                            const isEditing = editingMessageId === message.id
                            const canEditDelete = canEditDeleteMessage(message)

                            return (
                              <div
                                key={message.id}
                                className={`flex ${message.isFromGuest ? 'justify-end' : 'justify-start'}`}
                              >
                                <div className="relative group max-w-[85%]">
                                  <div
                                    className={`rounded-lg p-3 ${message.isFromGuest
                                      ? 'bg-primary text-white'
                                      : 'bg-white border border-gray-200 text-gray-900'
                                      }`}
                                    style={
                                      message.isFromGuest
                                        ? { backgroundColor: BRAND_COLORS.primary }
                                        : {}
                                    }
                                  >
                                    <div className="flex items-start justify-between gap-2 mb-1">
                                      <p className="text-xs font-medium opacity-80">
                                        {message.isFromGuest
                                          ? 'You'
                                          : message.user?.name || 'Hotel Staff'}
                                      </p>
                                      {canEditDelete && !isEditing && (
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                          <Button
                                            onClick={() => handleEditMessage(message)}
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0 hover:bg-black/10"
                                            title="Edit message"
                                          >
                                            <Edit className="h-3 w-3" />
                                          </Button>
                                          <Button
                                            onClick={() => handleDeleteMessage(message.id)}
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0 hover:bg-red-500/20 text-red-600"
                                            title="Delete message"
                                            disabled={deletingMessageId === message.id}
                                          >
                                            {deletingMessageId === message.id ? (
                                              <MoonLoader size="sm" />
                                            ) : (
                                              <Trash2 className="h-3 w-3" />
                                            )}
                                          </Button>
                                        </div>
                                      )}
                                    </div>

                                    {isEditing ? (
                                      <div className="space-y-2">
                                        {/* Show current image or new image preview */}
                                        {editingNewImagePreview ? (
                                          <div className="mb-2 relative">
                                            <p className="text-xs opacity-70 mb-1">New image:</p>
                                            <img
                                              src={editingNewImagePreview}
                                              alt="New attachment"
                                              className="max-w-full max-h-[200px] rounded-lg object-contain border-2 border-gray-200"
                                            />
                                            <Button
                                              onClick={handleRemoveEditImage}
                                              variant="ghost"
                                              size="sm"
                                              className="absolute top-6 right-2 h-6 w-6 p-0 bg-red-500/80 hover:bg-red-500 text-white"
                                              title="Remove new image"
                                            >
                                              <X className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        ) : editingMessageImageUrl && (
                                          <div className="mb-2 relative">
                                            <p className="text-xs opacity-70 mb-1">Current image:</p>
                                            <img
                                              src={editingMessageImageUrl}
                                              alt="Message attachment"
                                              className="max-w-full max-h-[200px] rounded-lg object-contain border-2 border-gray-200"
                                              onError={(e) => {
                                                e.currentTarget.style.display = 'none'
                                              }}
                                            />
                                          </div>
                                        )}

                                        {/* Image upload input */}
                                        <div className="mb-2">
                                          <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                              type="file"
                                              accept="image/*"
                                              onChange={handleEditImageSelect}
                                              className="hidden"
                                              id={`edit-image-${message.id}`}
                                            />
                                            <Button
                                              type="button"
                                              variant="outline"
                                              size="sm"
                                              className="h-8 text-gray-800"
                                              onClick={() => document.getElementById(`edit-image-${message.id}`)?.click()}
                                            >
                                              <ImageIcon className="h-4 w-4 mr-1" />
                                              {editingNewImage ? 'Change Image' : editingMessageImageUrl ? 'Replace Image' : 'Add Image'}
                                            </Button>
                                          </label>
                                        </div>

                                        <Textarea
                                          value={editingMessageText}
                                          onChange={(e) => setEditingMessageText(e.target.value)}
                                          className="min-h-[80px] resize-none text-black placeholder:text-gray-500"
                                          autoFocus
                                          placeholder="Type your message..."
                                        />
                                        <div className="flex gap-2 justify-end">
                                          <Button
                                            onClick={handleCancelEditMessage}
                                            variant="ghost"
                                            size="sm"
                                            className="h-7"
                                          >
                                            Cancel
                                          </Button>
                                          <Button
                                            onClick={() => handleSaveEdit(message.id)}
                                            size="sm"
                                            className="h-7"
                                            style={{ backgroundColor: BRAND_COLORS.primary }}
                                          >
                                            Save
                                          </Button>
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        {imageUrl && (
                                          <div className="mb-2">
                                            <img
                                              src={imageUrl}
                                              alt="Message attachment"
                                              className="max-w-full max-h-[300px] rounded-lg object-contain cursor-pointer hover:opacity-90 transition-opacity border-2 border-white/20"
                                              onError={(e) => {
                                                e.currentTarget.style.display = 'none'
                                              }}
                                              onLoad={(e) => {
                                                e.currentTarget.style.display = 'block'
                                              }}
                                              onClick={() => {
                                                const newWindow = window.open()
                                                if (newWindow) {
                                                  newWindow.document.write(`<img src="${imageUrl}" style="max-width: 100%; height: auto;" />`)
                                                }
                                              }}
                                            />
                                          </div>
                                        )}
                                        {textWithoutImage && textWithoutImage.trim() && (
                                          <p className="text-sm whitespace-pre-wrap break-words">
                                            {textWithoutImage}
                                          </p>
                                        )}
                                        <p className="text-xs mt-2 opacity-70">
                                          {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>

                    {/* Message Input */}
                    <div className="p-4 border-t bg-white">
                      {/* Image Preview */}
                      {imagePreview && (
                        <div className="mb-2 relative inline-block">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="max-w-[200px] max-h-[200px] rounded-lg border-2 border-gray-200"
                          />
                          <Button
                            onClick={handleRemoveImage}
                            variant="destructive"
                            size="sm"
                            className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageSelect}
                          className="hidden"
                          id="image-upload-sidebar"
                          disabled={sendingMessage || !currentGuestId}
                        />
                        <label htmlFor="image-upload-sidebar">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="px-3 self-end"
                            disabled={sendingMessage || !currentGuestId}
                            asChild
                          >
                            <span>
                              <Paperclip className="h-4 w-4" />
                            </span>
                          </Button>
                        </label>
                        <Textarea
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type your message..."
                          className="flex-1 min-h-[60px] resize-none text-sm text-black placeholder:text-gray-500"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault()
                              handleSendMessage()
                            }
                          }}
                          disabled={sendingMessage}
                        />
                        <Button
                          onClick={handleSendMessage}
                          disabled={(!newMessage.trim() && !selectedImage) || sendingMessage}
                          className="px-4 self-end"
                          style={{ backgroundColor: BRAND_COLORS.primary }}
                        >
                          {sendingMessage ? (
                            <MoonLoader />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Booking Details Dialog - Premium Design */}
        <Dialog open={showBookingDetailsDialog} onOpenChange={setShowBookingDetailsDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] border-0 shadow-2xl rounded-2xl p-0 overflow-hidden flex flex-col">
            <DialogHeader className="p-6 text-white flex-shrink-0" style={{
              background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
            }}>
              <DialogTitle className="text-2xl font-bold flex items-center gap-3">
                <div className="p-2 rounded-lg bg-white/20 backdrop-blur-sm">
                  <Bed className="h-6 w-6" />
                </div>
                Booking Details
              </DialogTitle>
            </DialogHeader>
            {selectedBooking && (
              <div className="p-6 space-y-6 bg-white overflow-y-auto flex-1">
                <div className="grid grid-cols-2 gap-6">
                  <div className="p-4 rounded-xl border-2" style={{ borderColor: `${BRAND_COLORS.primary}40`, backgroundColor: `${BRAND_COLORS.accent}20` }}>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Reservation Number</p>
                    <p className="text-base font-bold" style={{ color: BRAND_COLORS.dark }}>{selectedBooking.reservationNumber || 'N/A'}</p>
                  </div>
                  <div className="p-4 rounded-xl border-2" style={{ borderColor: `${BRAND_COLORS.primary}40`, backgroundColor: `${BRAND_COLORS.accent}20` }}>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Status</p>
                    <div>{getStatusBadge(selectedBooking.status)}</div>
                  </div>
                  <div className="p-4 rounded-xl border-2" style={{ borderColor: `${BRAND_COLORS.primary}40`, backgroundColor: `${BRAND_COLORS.accent}20` }}>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Room</p>
                    <p className="text-base font-semibold" style={{ color: BRAND_COLORS.dark }}>
                      Room {selectedBooking.room?.roomNumber} - {selectedBooking.room?.roomType?.name}
                    </p>
                  </div>
                  <div className="p-4 rounded-xl border-2" style={{ borderColor: `${BRAND_COLORS.primary}40`, backgroundColor: `${BRAND_COLORS.accent}20` }}>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Guests</p>
                    <p className="text-base font-semibold" style={{ color: BRAND_COLORS.dark }}>{selectedBooking.numberOfGuests}</p>
                  </div>
                  <div className="p-4 rounded-xl border-2" style={{ borderColor: `${BRAND_COLORS.primary}40`, backgroundColor: `${BRAND_COLORS.accent}20` }}>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Check-in</p>
                    <p className="text-base font-semibold" style={{ color: BRAND_COLORS.dark }}>
                      {new Date(selectedBooking.checkInDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="p-4 rounded-xl border-2" style={{ borderColor: `${BRAND_COLORS.primary}40`, backgroundColor: `${BRAND_COLORS.accent}20` }}>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Check-out</p>
                    <p className="text-base font-semibold" style={{ color: BRAND_COLORS.dark }}>
                      {new Date(selectedBooking.checkOutDate).toLocaleDateString()}
                    </p>
                  </div>
                  {selectedBooking.totalAmount && (
                    <div className="col-span-2 p-5 rounded-xl border-2" style={{
                      borderColor: BRAND_COLORS.primary,
                      background: `linear-gradient(135deg, ${BRAND_COLORS.accent} 0%, white 100%)`,
                    }}>
                      <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Total Amount</p>
                      <p className="text-2xl font-bold" style={{ color: BRAND_COLORS.primary }}>
                        {formatCurrency(selectedBooking.totalAmount, selectedBooking.room?.currency || 'FRW')}
                      </p>
                    </div>
                  )}
                  {selectedBooking.specialRequests && (
                    <div className="col-span-2 p-4 rounded-xl border-2" style={{ borderColor: `${BRAND_COLORS.primary}40`, backgroundColor: `${BRAND_COLORS.accent}20` }}>
                      <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>Special Requests</p>
                      <p className="text-sm" style={{ color: BRAND_COLORS.dark }}>{selectedBooking.specialRequests}</p>
                    </div>
                  )}
                  {selectedBooking.status === 'CANCELLED' && selectedBooking.cancellationReason && (
                    <div className="col-span-2 p-4 rounded-xl border-2 border-red-300 bg-red-50">
                      <p className="text-xs font-semibold uppercase tracking-wide mb-2 text-red-700">Cancellation Reason</p>
                      <p className="text-sm text-red-900">{selectedBooking.cancellationReason}</p>
                      {selectedBooking.cancellationDate && (
                        <p className="text-xs text-red-600 mt-2">
                          Cancelled on: {new Date(selectedBooking.cancellationDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            <DialogFooter className="p-6 bg-gray-50 border-t flex-shrink-0 flex gap-3">
              {selectedBooking?.status === 'PENDING' && (
                <>
                  <Button
                    onClick={() => handleEditBooking(selectedBooking)}
                    className="rounded-lg shadow-md hover:shadow-lg transition-all font-semibold"
                    style={{
                      backgroundColor: BRAND_COLORS.primary,
                      color: 'white',
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Reservation
                  </Button>
                  <Button
                    onClick={() => setShowCancelDialog(true)}
                    variant="destructive"
                    className="rounded-lg shadow-md hover:shadow-lg transition-all font-semibold"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel Reservation
                  </Button>
                </>
              )}
              {selectedBooking && (selectedBooking.status === 'CHECKED_OUT' || selectedBooking.status === 'CONFIRMED') && !hasReview(selectedBooking.id) && (
                <Button
                  onClick={() => {
                    setShowBookingDetailsDialog(false)
                    handleOpenReviewDialog(selectedBooking)
                  }}
                  className="rounded-lg shadow-md hover:shadow-lg transition-all font-semibold"
                  style={{
                    backgroundColor: BRAND_COLORS.primary,
                    color: 'white',
                  }}
                >
                  <Star className="h-4 w-4 mr-2" />
                  Write Review
                </Button>
              )}
              <Button
                onClick={() => setShowBookingDetailsDialog(false)}
                className="rounded-lg shadow-md hover:shadow-lg transition-all font-semibold"
                style={{
                  background: `linear-gradient(135deg, ${BRAND_COLORS.primary} 0%, ${BRAND_COLORS.secondary} 100%)`,
                  color: 'white',
                }}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Booking Dialog - Similar to accommodation.tsx */}
        <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-3xl md:text-4xl font-bold text-center mb-8" style={{ color: '#2C2416' }}>
                IkoroHouse Booking form
              </DialogTitle>
              {selectedRoom && (
                <DialogDescription className="text-center mb-6">
                  {selectedRoom.title || selectedRoom.roomNumber}
                  {selectedRoom.company && ` at ${selectedRoom.company.name}`}
                </DialogDescription>
              )}
            </DialogHeader>
            {selectedRoom && (
              <form onSubmit={(e) => { e.preventDefault(); handleBookingSubmit(); }} className="space-y-6">
                {/* ARRIVING DATE */}
                <div className="flex flex-col md:flex-row md:items-center gap-12">
                  <label className="w-full md:w-48 font-semibold" style={{ color: '#2C2416' }}>
                    ARRIVING DATE:
                  </label>
                  <Input
                    type="date"
                    placeholder="dd/mm/yyyy"
                    value={bookingForm.checkInDate}
                    onChange={(e) => {
                      setBookingForm({ ...bookingForm, checkInDate: e.target.value })
                      // Auto-apply promotion when dates change
                      if (e.target.value && bookingForm.checkOutDate) {
                        autoApplyPromotion(e.target.value, bookingForm.checkOutDate)
                      }
                    }}
                    min={new Date().toISOString().split('T')[0]}
                    required
                    className="flex-1 bg-white border border-gray-300 rounded-none"
                  />
                </div>

                {/* DEPARTURE DATE */}
                <div className="flex flex-col md:flex-row md:items-center gap-12">
                  <label className="w-full md:w-48 font-semibold" style={{ color: '#2C2416' }}>
                    DEPARTURE DATE:
                  </label>
                  <Input
                    type="date"
                    placeholder="dd/mm/yyyy"
                    value={bookingForm.checkOutDate}
                    onChange={(e) => {
                      setBookingForm({ ...bookingForm, checkOutDate: e.target.value })
                      // Auto-apply promotion when dates change
                      if (bookingForm.checkInDate && e.target.value) {
                        autoApplyPromotion(bookingForm.checkInDate, e.target.value)
                      }
                    }}
                    min={bookingForm.checkInDate || new Date().toISOString().split('T')[0]}
                    required
                    className="flex-1 bg-white border border-gray-300 rounded-none"
                  />
                </div>

                {/* NUMBER OF GUESTS */}
                <div className="flex flex-col md:flex-row md:items-center gap-12">
                  <label className="w-full md:w-48 font-semibold" style={{ color: '#2C2416' }}>
                    NUMBER OF GUESTS:
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max={selectedRoom.capacity}
                    value={bookingForm.numberOfGuests}
                    onChange={(e) => setBookingForm({ ...bookingForm, numberOfGuests: e.target.value })}
                    required
                    className="flex-1 bg-white border border-gray-300 rounded-none"
                  />
                  <p className="text-xs text-gray-500 mt-1 md:mt-0 md:ml-2">
                    Maximum capacity: {selectedRoom.capacity} {selectedRoom.capacity === 1 ? 'guest' : 'guests'}
                  </p>
                </div>

                {/* AUTOMATIC PROMOTIONS DISPLAY */}
                {bookingForm.checkInDate && bookingForm.checkOutDate && appliedPromotions.length > 0 && (
                  <div className="flex flex-col md:flex-row md:items-start gap-12">
                    <label className="w-full md:w-48 font-semibold pt-2" style={{ color: '#2C2416' }}>
                      APPLIED PROMOTIONS:
                    </label>
                    <div className="flex-1 space-y-2">
                      {appliedPromotions.map((promo: any, index: number) => (
                        <div key={promo.id} className="bg-green-50 border border-green-200 p-4 rounded">
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-green-100 rounded-full">
                              <Gift className="h-5 w-5 text-green-700" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-semibold text-green-800">
                                  ✓ {promo.name} - Automatically Applied
                                </p>
                                {appliedPromotions.length > 1 && (
                                  <Badge variant="secondary" className="bg-green-200 text-green-800 text-xs">
                                    {index + 1} of {appliedPromotions.length}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-green-700 mt-1">
                                {promo.description || 'This promotion has been automatically applied to your booking'}
                              </p>
                              <div className="mt-2 text-xs text-green-600 font-semibold">
                                {promo.discountType === 'PERCENTAGE'
                                  ? `${promo.discountValue}% discount`
                                  : promo.discountType === 'FIXED_AMOUNT'
                                    ? `${promo.discountValue} ${selectedRoom.currency || 'FRW'} discount`
                                    : `${promo.discountValue} free night(s)`
                                }
                                {promo.calculatedDiscount && (
                                  <span className="ml-2">(-{promo.calculatedDiscount.toLocaleString()} {selectedRoom.currency || 'FRW'})</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {appliedPromotions.length > 1 && (
                        <div className="bg-blue-50 border border-blue-200 p-3 rounded mt-2">
                          <p className="text-xs font-semibold text-blue-800">
                            💡 All {appliedPromotions.length} promotions have been automatically applied and combined for maximum savings!
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* SPECIAL REQUESTS */}
                <div className="flex flex-col md:flex-row md:items-start gap-12">
                  <label className="w-full md:w-48 font-semibold pt-2" style={{ color: '#2C2416' }}>
                    SPECIAL REQUESTS:
                  </label>
                  <Textarea
                    value={bookingForm.specialRequests}
                    onChange={(e) => setBookingForm({ ...bookingForm, specialRequests: e.target.value })}
                    className="flex-1 bg-white border border-gray-300 rounded-none p-2 min-h-[80px]"
                    placeholder="Any special requests or notes..."
                  />
                </div>

                {/* PRICE BREAKDOWN */}
                {bookingForm.checkInDate && bookingForm.checkOutDate && (
                  <div className="flex flex-col md:flex-row md:items-start gap-12">
                    <div className="w-full md:w-48 font-semibold" style={{ color: '#2C2416' }}>
                      PRICE BREAKDOWN:
                    </div>
                    <div className="flex-1 bg-gray-50 p-4 space-y-2">
                      {(() => {
                        const nights = Math.ceil(
                          (new Date(bookingForm.checkOutDate).getTime() - new Date(bookingForm.checkInDate).getTime()) / (1000 * 60 * 60 * 24)
                        )
                        const baseAmount = (selectedRoom.basePricePerNight || 0) * nights
                        let discountAmount = 0
                        let totalAmount = baseAmount

                        // Calculate discount from all applied promotions
                        const promotionDiscounts: { promo: any; discount: number }[] = []
                        if (appliedPromotions.length > 0) {
                          let remainingAmount = baseAmount
                          appliedPromotions.forEach((promo: any) => {
                            let promoDiscount = 0
                            if (promo.discountType === 'PERCENTAGE') {
                              // Apply percentage to remaining amount
                              promoDiscount = remainingAmount * (parseFloat(promo.discountValue.toString()) / 100)
                              remainingAmount = remainingAmount - promoDiscount
                            } else if (promo.discountType === 'FIXED_AMOUNT') {
                              promoDiscount = parseFloat(promo.discountValue.toString())
                              remainingAmount = Math.max(0, remainingAmount - promoDiscount)
                            } else if (promo.discountType === 'FREE_NIGHT') {
                              const freeNights = parseInt(promo.discountValue.toString())
                              promoDiscount = (selectedRoom.basePricePerNight || 0) * freeNights
                              remainingAmount = Math.max(0, remainingAmount - promoDiscount)
                            }
                            discountAmount += promoDiscount
                            // Store the calculated discount for this promotion
                            promotionDiscounts.push({ promo, discount: promoDiscount })
                          })
                          totalAmount = remainingAmount
                        }

                        return (
                          <>
                            <div className="flex justify-between text-sm">
                              <span>Base Price ({nights} {nights === 1 ? 'night' : 'nights'} × {selectedRoom.basePricePerNight?.toLocaleString()} {selectedRoom.currency || 'FRW'}):</span>
                              <span className="font-semibold">{baseAmount.toLocaleString()} {selectedRoom.currency || 'FRW'}</span>
                            </div>
                            {promotionDiscounts.length > 0 && (
                              <div className="space-y-1">
                                {promotionDiscounts.map((item, index: number) => {
                                  const { promo, discount } = item
                                  return (
                                    <div key={promo.id} className="flex justify-between text-sm text-green-600">
                                      <span>Promotion {index + 1}: {promo.name}</span>
                                      <span className="font-semibold">
                                        {discount > 0 ? `-${discount.toLocaleString()}` : discount.toLocaleString()} {selectedRoom.currency || 'FRW'}
                                      </span>
                                    </div>
                                  )
                                })}
                                {promotionDiscounts.length > 1 && (
                                  <div className="flex justify-between text-sm font-bold text-green-700 pt-1 border-t border-green-300">
                                    <span>Total Promotions Discount:</span>
                                    <span>-{discountAmount.toLocaleString()} {selectedRoom.currency || 'FRW'}</span>
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-300">
                              <span>Total Amount:</span>
                              <span style={{ color: BRAND_COLORS.primary }}>{totalAmount.toLocaleString()} {selectedRoom.currency || 'FRW'}</span>
                            </div>
                          </>
                        )
                      })()}
                    </div>
                  </div>
                )}

                {/* SUBMIT BUTTON */}
                <div className="flex flex-col md:flex-row md:items-center gap-6 pt-6">
                  <div className="w-full md:w-48"></div>
                  <div className="flex-1 flex gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowBookingDialog(false)}
                      className="flex-1 bg-white text-black font-semibold uppercase hover:opacity-90 transition-opacity py-3 rounded-none"
                    >
                      CANCEL
                    </Button>
                    <Button
                      type="submit"
                      disabled={isCreatingReservation || !guestId}
                      className="flex-1 bg-black text-white font-semibold uppercase hover:opacity-90 transition-opacity py-3 rounded-none"
                    >
                      {isCreatingReservation ? 'BOOKING...' : 'SUBMIT'}
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Cancel Reservation Confirmation Dialog */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                Cancel Reservation
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to cancel this reservation? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="cancelReason">Cancellation Reason (Optional)</Label>
                <Textarea
                  id="cancelReason"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Please provide a reason for cancellation..."
                  className="mt-2 min-h-[80px]"
                />
              </div>
              {selectedBooking && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Reservation:</strong> {selectedBooking.reservationNumber}<br />
                    <strong>Room:</strong> Room {selectedBooking.room?.roomNumber}<br />
                    <strong>Dates:</strong> {new Date(selectedBooking.checkInDate).toLocaleDateString()} - {new Date(selectedBooking.checkOutDate).toLocaleDateString()}
                  </AlertDescription>
                </Alert>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCancelDialog(false)
                  setCancelReason('')
                }}
                disabled={isCancelling}
              >
                Keep Reservation
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancelReservation}
                disabled={isCancelling}
              >
                {isCancelling ? (
                  <>
                    <MoonLoader size="sm" color="gray" className="mr-2" />
                    Cancelling...
                  </>
                ) : (
                  <>
                    <X className="h-4 w-4 mr-2" />
                    Confirm Cancellation
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Booking Dialog */}
        <Dialog open={showEditBookingDialog} onOpenChange={setShowEditBookingDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                  <Edit className="h-5 w-5" style={{ color: BRAND_COLORS.primary }} />
                </div>
                Edit Reservation
              </DialogTitle>
              <DialogDescription>
                Update your reservation details. Only pending reservations can be edited.
              </DialogDescription>
            </DialogHeader>
            {selectedBooking && (
              <form onSubmit={(e) => { e.preventDefault(); handleUpdateBooking(); }} className="space-y-6 py-4">
                {/* Reservation Info */}
                <div className="p-4 rounded-lg border-2" style={{ borderColor: `${BRAND_COLORS.primary}40`, backgroundColor: `${BRAND_COLORS.accent}20` }}>
                  <p className="text-sm font-semibold mb-2" style={{ color: BRAND_COLORS.dark }}>Reservation Number</p>
                  <p className="text-base font-bold" style={{ color: BRAND_COLORS.primary }}>{selectedBooking.reservationNumber}</p>
                  <p className="text-sm mt-2" style={{ color: BRAND_COLORS.dark, opacity: 0.7 }}>
                    Room: {selectedBooking.room?.roomNumber} - {selectedBooking.room?.roomType?.name}
                  </p>
                </div>

                {/* Check-in Date */}
                <div className="space-y-2">
                  <Label htmlFor="editCheckInDate" className="text-sm font-semibold" style={{ color: BRAND_COLORS.dark }}>
                    Check-in Date *
                  </Label>
                  <Input
                    id="editCheckInDate"
                    type="date"
                    value={editBookingForm.checkInDate}
                    onChange={(e) => {
                      setEditBookingForm({ ...editBookingForm, checkInDate: e.target.value })
                      setEditBookingErrors({ ...editBookingErrors, checkInDate: '' })
                    }}
                    min={new Date().toISOString().split('T')[0]}
                    className="p-3 rounded-lg border-2"
                    style={{ borderColor: editBookingErrors.checkInDate ? '#ef4444' : BRAND_COLORS.primary }}
                    required
                  />
                  {editBookingErrors.checkInDate && (
                    <p className="text-sm text-red-600">{editBookingErrors.checkInDate}</p>
                  )}
                </div>

                {/* Check-out Date */}
                <div className="space-y-2">
                  <Label htmlFor="editCheckOutDate" className="text-sm font-semibold" style={{ color: BRAND_COLORS.dark }}>
                    Check-out Date *
                  </Label>
                  <Input
                    id="editCheckOutDate"
                    type="date"
                    value={editBookingForm.checkOutDate}
                    onChange={(e) => {
                      setEditBookingForm({ ...editBookingForm, checkOutDate: e.target.value })
                      setEditBookingErrors({ ...editBookingErrors, checkOutDate: '' })
                    }}
                    min={editBookingForm.checkInDate || new Date().toISOString().split('T')[0]}
                    className="p-3 rounded-lg border-2"
                    style={{ borderColor: editBookingErrors.checkOutDate ? '#ef4444' : BRAND_COLORS.primary }}
                    required
                  />
                  {editBookingErrors.checkOutDate && (
                    <p className="text-sm text-red-600">{editBookingErrors.checkOutDate}</p>
                  )}
                </div>

                {/* Number of Guests */}
                <div className="space-y-2">
                  <Label htmlFor="editNumberOfGuests" className="text-sm font-semibold" style={{ color: BRAND_COLORS.dark }}>
                    Number of Guests *
                  </Label>
                  <Input
                    id="editNumberOfGuests"
                    type="number"
                    min="1"
                    value={editBookingForm.numberOfGuests}
                    onChange={(e) => {
                      setEditBookingForm({ ...editBookingForm, numberOfGuests: e.target.value })
                      setEditBookingErrors({ ...editBookingErrors, numberOfGuests: '' })
                    }}
                    className="p-3 rounded-lg border-2"
                    style={{ borderColor: editBookingErrors.numberOfGuests ? '#ef4444' : BRAND_COLORS.primary }}
                    required
                  />
                  {editBookingErrors.numberOfGuests && (
                    <p className="text-sm text-red-600">{editBookingErrors.numberOfGuests}</p>
                  )}
                </div>

                {/* Special Requests */}
                <div className="space-y-2">
                  <Label htmlFor="editSpecialRequests" className="text-sm font-semibold" style={{ color: BRAND_COLORS.dark }}>
                    Special Requests
                  </Label>
                  <Textarea
                    id="editSpecialRequests"
                    value={editBookingForm.specialRequests}
                    onChange={(e) => setEditBookingForm({ ...editBookingForm, specialRequests: e.target.value })}
                    placeholder="Any special requests or notes..."
                    className="p-3 rounded-lg border-2 min-h-[100px]"
                    style={{ borderColor: BRAND_COLORS.primary }}
                  />
                </div>

                <DialogFooter className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowEditBookingDialog(false)
                      setEditBookingForm({
                        checkInDate: '',
                        checkOutDate: '',
                        numberOfGuests: '1',
                        specialRequests: '',
                      })
                      setEditBookingErrors({})
                      setSelectedBooking(null)
                    }}
                    disabled={isUpdatingBooking}
                    className="rounded-lg"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isUpdatingBooking}
                    className="rounded-lg shadow-md hover:shadow-lg transition-all font-semibold"
                    style={{
                      backgroundColor: BRAND_COLORS.primary,
                      color: 'white',
                    }}
                  >
                    {isUpdatingBooking ? (
                      <>
                        <MoonLoader size="sm" className="mr-2" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Update Reservation
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Review Dialog */}
        <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] border-0 shadow-2xl rounded-2xl p-0 overflow-hidden flex flex-col">
            <DialogHeader className="p-6 text-white flex-shrink-0" style={{
              background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
            }}>
              <DialogTitle className="text-2xl font-bold flex items-center gap-3">
                <div className="p-2 rounded-lg bg-white/20 backdrop-blur-sm">
                  <Star className="h-6 w-6" />
                </div>
                Write a Review
              </DialogTitle>
              <DialogDescription className="text-white/90 mt-2">
                {selectedBooking && (
                  <span>Room {selectedBooking.room?.roomNumber} - {selectedBooking.room?.roomType?.name || selectedBooking.room?.title}</span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="p-6 space-y-6 bg-white overflow-y-auto flex-1">
              <div className="space-y-4">
                <div>
                  <Label className="text-base font-semibold mb-3 block" style={{ color: BRAND_COLORS.dark }}>
                    Rating *
                  </Label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => {
                          setReviewForm({ ...reviewForm, rating })
                          setReviewErrors({ ...reviewErrors, rating: '' })
                        }}
                        className="focus:outline-none transition-transform hover:scale-110"
                      >
                        <Star
                          className={`w-10 h-10 transition-colors ${rating <= reviewForm.rating
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-gray-300'
                            }`}
                        />
                      </button>
                    ))}
                    <span className="ml-3 text-lg font-semibold" style={{ color: BRAND_COLORS.primary }}>
                      {reviewForm.rating} / 5
                    </span>
                  </div>
                  {reviewErrors.rating && (
                    <p className="text-xs text-red-500 mt-1">{reviewErrors.rating}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="review-comment" className="text-base font-semibold mb-3 block" style={{ color: BRAND_COLORS.dark }}>
                    Your Review (Optional)
                  </Label>
                  <Textarea
                    id="review-comment"
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    placeholder="Share your experience with this stay..."
                    rows={6}
                    className="border-2 rounded-lg resize-none"
                    style={{ borderColor: `${BRAND_COLORS.primary}40` }}
                  />
                </div>
              </div>
            </div>
            <DialogFooter className="p-6 bg-gray-50 border-t flex-shrink-0 flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowReviewDialog(false)
                  setReviewForm({ rating: 5, comment: '' })
                  setReviewErrors({})
                  setSelectedBooking(null)
                }}
                className="rounded-lg shadow-md hover:shadow-lg transition-all font-semibold"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateReview}
                disabled={isCreatingReview}
                className="rounded-lg shadow-md hover:shadow-lg transition-all font-semibold"
                style={{
                  background: `linear-gradient(135deg, ${BRAND_COLORS.primary} 0%, ${BRAND_COLORS.secondary} 100%)`,
                  color: 'white',
                }}
              >
                {isCreatingReview ? (
                  <>
                    <MoonLoader size="sm" className="mr-2" color="gray" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Star className="h-4 w-4 mr-2" />
                    Submit Review
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}